/**
 * Localization strings for the preferences menu
 *
 * @module
 */

import { createDictionary } from './utils';

// Refer to "Guidelines for Programmers" in ./README.md before editing this file

export const preferencesText = createDictionary({
  preferences: {
    'en-us': 'Preferences',
    'ru-ru': 'Настройки',
    'es-es': 'Preferencias',
    'fr-fr': 'Préférences',
    'uk-ua': 'Уподобання',
    'de-ch': 'Einstellungen',
    'pt-br': 'Preferências',
  },
  customization: {
    'en-us': 'Customization',
    'ru-ru': 'Настройка',
    'es-es': 'Personalización',
    'fr-fr': 'Personnalisation',
    'uk-ua': 'Спеціальнізація',
    'de-ch': 'Anpassung',
    'pt-br': 'Personalização',
  },
  userPreferences: {
    'en-us': 'User Preferences',
    'ru-ru': 'Пользовательские настройки',
    'es-es': 'Preferencias del usuario',
    'fr-fr': "Préférences de l'utilisateur",
    'uk-ua': 'Налаштування користувача',
    'de-ch': 'Benutzereinstellungen',
    'pt-br': 'Preferências do usuário',
  },
  defaultUserPreferences: {
    'en-us': 'Default User Preferences',
    'ru-ru': 'Настройки пользователя по умолчанию',
    'es-es': 'Preferencias de usuario predeterminadas',
    'fr-fr': 'Préférences utilisateur par défaut',
    'uk-ua': 'Параметри користувача за умовчанням',
    'de-ch': 'Standardbenutzereinstellungen',
    'pt-br': 'Preferências padrão do usuário',
  },
  general: {
    'en-us': 'General',
    'ru-ru': 'Общий',
    'es-es': 'General',
    'fr-fr': 'Image personnalisée',
    'uk-ua': 'Спеціальне зображення',
    'de-ch': 'Allgemein',
    'pt-br': 'Em geral',
  },
  ui: {
    'en-us': 'User Interface',
    'ru-ru': 'Пользовательский интерфейс',
    'es-es': 'Interfaz de usuario',
    'fr-fr': 'Interface utilisateur',
    'uk-ua': 'Інтерфейс користувача',
    'de-ch': 'Benutzeroberfläche',
    'pt-br': 'Interface do usuário',
  },
  theme: {
    'en-us': 'Theme',
    'ru-ru': 'Тема',
    'es-es': 'Tema',
    'fr-fr': 'Thème',
    'uk-ua': 'Тема',
    'de-ch': 'Thema',
    'pt-br': 'Tema',
  },
  useSystemSetting: {
    'en-us': 'Use system setting',
    'ru-ru': 'Использовать системные настройки',
    'es-es': 'Utilizar la configuración del sistema',
    'fr-fr': 'Utiliser les paramètres du système',
    'uk-ua': 'Використовуйте налаштування системи',
    'de-ch': 'Systemeinstellung verwenden',
    'pt-br': 'Usar as configurações do sistema',
  },
  inheritOsSettings: {
    'en-us': 'Copies value from your Operating System settings',
    'ru-ru': 'Копирует значение из настроек вашей операционной системы.',
    'es-es': 'Copia el valor de la configuración de su sistema operativo',
    'fr-fr': "Copie la valeur des paramètres de votre système d'exploitation",
    'uk-ua': 'Копіює значення з налаштувань вашої операційної системи',
    'de-ch': 'Übernimmt den Wert aus Ihren Betriebssystemeinstellungen',
    'pt-br': 'Copia o valor das configurações do seu sistema operacional.',
  },
  light: {
    comment: 'Light mode',
    'en-us': 'Light',
    'ru-ru': 'Свет',
    'es-es': 'Claro',
    'fr-fr': 'Lumière',
    'uk-ua': 'світло',
    'de-ch': 'Hell',
    'pt-br': 'Luz',
  },
  dark: {
    comment: 'Dark mode',
    'en-us': 'Dark',
    'ru-ru': 'Темный',
    'es-es': 'Oscuro',
    'fr-fr': 'Sombre',
    'uk-ua': 'Темний',
    'de-ch': 'Dunkel',
    'pt-br': 'Escuro',
  },
  reduceMotion: {
    'en-us': 'Reduce motion',
    'ru-ru': 'Уменьшите движение',
    'es-es': 'Reducir el movimiento',
    'fr-fr': 'Réduire les mouvements',
    'uk-ua': 'Зменшити рух',
    'de-ch': 'Bewegung reduzieren',
    'pt-br': 'Reduzir movimento',
  },
  reduceMotionDescription: {
    'en-us': 'Disable non-essential animations and transitions.',
    'ru-ru': 'Отключите несущественные анимации и переходы.',
    'es-es': 'Desactivar animaciones y transiciones no esenciales.',
    'fr-fr': 'Désactivez les animations et les transitions non essentielles.',
    'uk-ua': "Вимкніть необов'язкову анімацію та переходи.",
    'de-ch': 'Nicht erforderliche Animationen und Übergänge deaktivieren.',
    'pt-br': 'Desative animações e transições não essenciais.',
  },
  rankThreshold: {
    'en-us': 'Show object count below',
    'de-ch': 'Objektanzahl unten anzeigen',
    'es-es': 'Mostrar el recuento de objetos a continuación',
    'fr-fr': "Afficher le nombre d'objets ci-dessous",
    'pt-br': 'Mostrar a contagem de objetos abaixo',
    'ru-ru': 'Ниже показано количество объектов.',
    'uk-ua': "Показати кількість об'єктів нижче",
  },
  rankThresholdDescription: {
    'en-us':
      'Show the count of linked Collection Objects for all ranks below this one.',
    'ru-ru':
      'Показывать количество объектов коллекции для всех рангов, включая и ниже этого.',
    'es-es':
      'Mostrar la cantidad de objetos de colección para todos los rangos, incluidos y por debajo de este.',
    'fr-fr':
      "Afficher le nombre d'objets de collection pour tous les rangs, y compris et en dessous de celui-ci.",
    'uk-ua':
      "Показати кількість об'єктів колекції для всіх рангів, включаючи та нижче цього.",
    'de-ch':
      'Anzahl der Sammlungsobjekte für alle Ränge einschließlich und unterhalb dieses Rangs anzeigen.',
    'pt-br':
      'Mostrar a contagem de Objetos de Coleção para todos os ranks, incluindo e abaixo deste.',
  },
  reduceTransparency: {
    'en-us': 'Reduce transparency',
    'ru-ru': 'Снизить прозрачность',
    'es-es': 'Reducir la transparencia',
    'fr-fr': 'Réduire la transparence',
    'uk-ua': 'Зменшити прозорість',
    'de-ch': 'Transparenz reduzieren',
    'pt-br': 'Reduzir a transparência',
  },
  reduceTransparencyDescription: {
    'en-us':
      'Whether to disable translucent backgrounds for user interface components whenever possible (e.g. table headers in tree view).',
    'ru-ru':
      'Следует ли по возможности отключать полупрозрачный фон для компонентов пользовательского интерфейса (например, заголовки таблиц в древовидном представлении).',
    'es-es':
      'Si se deben deshabilitar los fondos translúcidos para los componentes de la interfaz de usuario siempre que sea posible (por ejemplo, encabezados de tabla en la vista de árbol).',
    'fr-fr':
      "S'il faut désactiver les arrière-plans translucides pour les composants de l'interface utilisateur chaque fois que possible (par exemple, les en-têtes de tableau dans l'arborescence).",
    'uk-ua':
      'Чи вимикати напівпрозорий фон для компонентів інтерфейсу користувача, коли це можливо (наприклад, заголовки таблиць у перегляді дерева).',
    'de-ch':
      'Durchsichtige Hintergründe für Benutzeroberflächenkomponenten wann immer möglich deaktivieren (z. B. Tabellenüberschriften in der Baumansicht).',
    'pt-br':
      'Desativar ou não os fundos translúcidos para componentes da interface do usuário sempre que possível (por exemplo, cabeçalhos de tabela em uma visualização em árvore).',
  },
  contrast: {
    'en-us': 'Contrast',
    'ru-ru': 'Контраст',
    'es-es': 'Contraste',
    'fr-fr': 'Contraste',
    'uk-ua': 'Контраст',
    'de-ch': 'Kontrast',
    'pt-br': 'Contraste',
  },
  increase: {
    'en-us': 'Increase',
    'ru-ru': 'Увеличивать',
    'es-es': 'Aumentar',
    'fr-fr': 'Augmenter',
    'uk-ua': 'Збільшити',
    'de-ch': 'Erhöhen',
    'pt-br': 'Aumentar',
  },
  reduce: {
    'en-us': 'Reduce',
    'ru-ru': 'Уменьшать',
    'es-es': 'Reducir',
    'fr-fr': 'Réduire',
    'uk-ua': 'Зменшити',
    'de-ch': 'Verringern',
    'pt-br': 'Reduzir',
  },
  noPreference: {
    'en-us': 'No preference',
    'ru-ru': 'Без предпочтений',
    'es-es': 'Sin preferencia',
    'fr-fr': 'Pas de préférence',
    'uk-ua': 'Без переваг',
    'de-ch': 'Keine Präferenz',
    'pt-br': 'Sem preferência',
  },
  fontSize: {
    'en-us': 'Font size',
    'ru-ru': 'размер шрифта',
    'es-es': 'Tamaño de fuente',
    'fr-fr': 'Taille de police',
    'uk-ua': 'Розмір шрифту',
    'de-ch': 'Schriftgrösse',
    'pt-br': 'Tamanho da fonte',
  },
  fontFamily: {
    'en-us': 'Font family',
    'ru-ru': 'Семейство шрифтов',
    'es-es': 'Familia de fuentes',
    'fr-fr': 'Famille de polices',
    'uk-ua': 'Сімейство шрифтів',
    'de-ch': 'Schrift-Familie',
    'pt-br': 'Família de fontes',
  },
  fontFamilyDescription: {
    'en-us':
      'You can specify any font that is on your computer, even if it is not in the list. A comma-separated list of fonts is also supported, where each subsequent font will be used if the previous one is not available.',
    'ru-ru':
      'Вы можете указать любой шрифт, имеющийся на вашем компьютере, даже если его нет в списке. Поддерживается также список шрифтов, разделенных запятыми, при этом будет использоваться каждый последующий шрифт, если предыдущий недоступен.',
    'es-es':
      'Puede especificar cualquier fuente de su ordenador, incluso si no está en la lista. También se admite una lista de fuentes separadas por comas, donde se usará cada fuente subsiguiente si la anterior no está disponible.',
    'fr-fr':
      "Vous pouvez spécifier n'importe quelle police installée sur votre ordinateur, même si elle ne figure pas dans la liste. Les listes de polices séparées par des virgules sont également prises en charge ; si la police précédente est indisponible, la suivante sera utilisée.",
    'uk-ua':
      "Ви можете вказати будь-який шрифт, який є на вашому комп'ютері, навіть якщо його немає в списку. Також підтримується розділений комами список шрифтів, у якому використовуватиметься другий шрифт, якщо перший недоступний тощо.",
    'de-ch':
      'Sie können jede Schriftart angeben, die sich auf Ihrem Computer befindet, auch wenn diese nicht in der Liste enthalten ist. Eine durch Kommas getrennte Liste von Schriftarten wird ebenfalls unterstützt, wobei die zweite Schriftart verwendet wird, wenn die erste nicht verfügbar ist usw.',
    'pt-br':
      'Você pode especificar qualquer fonte que esteja instalada no seu computador, mesmo que ela não esteja na lista. Também é possível usar uma lista de fontes separadas por vírgulas, em que cada fonte subsequente será usada caso a anterior não esteja disponível.',
  },
  defaultFont: {
    'en-us': '(default font)',
    'ru-ru': '(шрифт по умолчанию)',
    'es-es': '(fuente predeterminada)',
    'fr-fr': '(police par défaut)',
    'uk-ua': '(типовий шрифт)',
    'de-ch': '(Standardschriftart)',
    'pt-br': '(fonte padrão)',
  },
  maxFormWidth: {
    'en-us': 'Max form width',
    'ru-ru': 'Максимальная ширина формы',
    'es-es': 'Ancho máximo del formulario',
    'fr-fr': 'Largeur maximale du formulaire',
    'uk-ua': 'Максимальна ширина форми',
    'de-ch': 'Maximale Formularbreite',
    'pt-br': 'Largura máxima do formulário',
  },
  fieldBackgrounds: {
    'en-us': 'Field backgrounds',
    'ru-ru': 'Полевые исследования',
    'es-es': 'Fondos de campo',
    'fr-fr': 'Milieux de terrain',
    'uk-ua': 'Польові фони',
    'de-ch': 'Feldhintergründe',
    'pt-br': 'Histórico de campo',
  },
  fieldBackground: {
    'en-us': 'Field background',
    'ru-ru': 'Полевой фон',
    'es-es': 'Fondo de campo',
    'fr-fr': 'Contexte du terrain',
    'uk-ua': 'Поле фону',
    'de-ch': 'Feldhintergrund',
    'pt-br': 'Histórico de campo',
  },
  disabledFieldBackground: {
    'en-us': 'Disabled field background',
    'ru-ru': 'Фоновое поле инвалидности',
    'es-es': 'Fondo de campo deshabilitado',
    'fr-fr': 'Fond de champ désactivé',
    'uk-ua': 'Вимкнений фон поля',
    'de-ch': 'Deaktivierter Feldhintergrund',
    'pt-br': 'fundo de campo desativado',
  },
  invalidFieldBackground: {
    'en-us': 'Invalid field background',
    'ru-ru': 'Недопустимое поле фона',
    'es-es': 'Fondo de campo no válido',
    'fr-fr': 'Fond de champ invalide',
    'uk-ua': 'Недійсний фон поля',
    'de-ch': 'Ungültiger Feldhintergrund',
    'pt-br': 'Fundo de campo inválido',
  },
  requiredFieldBackground: {
    'en-us': 'Required field background',
    'ru-ru': 'Требуемый опыт работы в данной области',
    'es-es': 'Fondo del campo obligatorio',
    'fr-fr': 'Contexte du champ obligatoire',
    'uk-ua': "Обов'язковий фон поля",
    'de-ch': 'Feldhintergrund erforderlich',
    'pt-br': 'Campo obrigatório de fundo',
  },
  darkFieldBackground: {
    'en-us': 'Field background (dark theme)',
    'ru-ru': 'Фоновое изображение поля (темная тема)',
    'es-es': 'Fondo de campo (tema oscuro)',
    'fr-fr': 'Fond de champ (thème sombre)',
    'uk-ua': 'Фон поля (темна тема)',
    'de-ch': 'Feldhintergrund (Dunkles Thema)',
    'pt-br': 'Fundo de campo (tema escuro)',
  },
  darkDisabledFieldBackground: {
    'en-us': 'Disabled field background (dark theme)',
    'ru-ru': 'Фоновое изображение поля отключено (темная тема).',
    'es-es': 'Fondo de campo deshabilitado (tema oscuro)',
    'fr-fr': 'Fond de champ désactivé (thème sombre)',
    'uk-ua': 'Вимкнений фон поля (темна тема)',
    'de-ch': 'Deaktivierter Feldhintergrund (Dunkles Thema)',
    'pt-br': 'Fundo de campo desativado (tema escuro)',
  },
  darkInvalidFieldBackground: {
    'en-us': 'Invalid field background (dark theme)',
    'ru-ru': 'Неверный фон поля (темная тема)',
    'es-es': 'Fondo de campo no válido (tema oscuro)',
    'fr-fr': 'Largeur de colonne de grille de sous-vue flexible',
    'uk-ua': 'Гнучка ширина стовпця сітки вкладеного перегляду',
    'de-ch': 'Ungültiger Feldhintergrund (Dunkles Thema)',
    'pt-br': 'Fundo de campo inválido (tema escuro)',
  },
  darkRequiredFieldBackground: {
    'en-us': 'Required field background (dark theme)',
    'ru-ru': 'Фоновое изображение обязательного поля (темная тема).',
    'es-es': 'Fondo del campo obligatorio (tema oscuro)',
    'fr-fr': 'Fond de champ obligatoire (thème sombre)',
    'uk-ua': 'Обов’язковий фон поля (темна тема)',
    'de-ch': 'Feldhintergrund erforderlich (Dunkles Thema)',
    'pt-br': 'Fundo de campo obrigatório (tema escuro)',
  },
  dialogs: {
    'en-us': 'Dialogs',
    'ru-ru': 'Диалоги',
    'es-es': 'Diálogos',
    'fr-fr': 'Boîtes de dialogue',
    'uk-ua': 'Діалоги',
    'de-ch': 'Dialoge',
    'pt-br': 'Diálogos',
  },
  appearance: {
    'en-us': 'Appearance',
    'ru-ru': 'Появление',
    'es-es': 'Apariencia',
    'fr-fr': 'Apparence',
    'uk-ua': 'Зовнішній вигляд',
    'de-ch': 'Aussehen',
    'pt-br': 'Aparência',
  },
  buttonsLight: {
    'en-us': 'Buttons (light mode)',
    'de-ch': 'Buttons (Helles Thema)',
    'es-es': 'Botones (modo luz)',
    'fr-fr': 'Boutons (mode lumière)',
    'ru-ru': 'Кнопки (световой режим)',
    'uk-ua': 'Кнопки (світлий режим)',
    'pt-br': 'Botões (modo claro)',
  },
  buttonsDark: {
    'en-us': 'Buttons (dark mode)',
    'de-ch': 'Buttons (Dunkles Thema)',
    'es-es': 'Botones (modo oscuro)',
    'fr-fr': 'Boutons (mode sombre)',
    'ru-ru': 'Кнопки (темный режим)',
    'uk-ua': 'Кнопки (темний режим)',
    'pt-br': 'Botões (modo escuro)',
  },
  translucentDialog: {
    'en-us': 'Translucent dialogs',
    'ru-ru': 'Полупрозрачные диалоги',
    'es-es': 'Diálogos translúcidos',
    'fr-fr': 'Dialogues translucides',
    'uk-ua': 'Напівпрозорі діалоги',
    'de-ch': 'Durchscheinende Dialoge',
    'pt-br': 'Diálogos translúcidos',
  },
  translucentDialogDescription: {
    'en-us': 'Whether dialogs have translucent background.',
    'ru-ru': 'Есть ли у диалогов полупрозрачный фон.',
    'es-es': 'Si los diálogos tienen fondo translúcido.',
    'fr-fr': 'Si les boîtes de dialogue ont un fond translucide.',
    'uk-ua': 'Чи мають діалоги прозорий фон.',
    'de-ch': 'Dialogfenster mit durchscheinenden Hintergrund.',
    'pt-br': 'Se os diálogos têm fundo translúcido.',
  },
  alwaysPrompt: {
    'en-us': 'Always prompt to choose collection',
    'ru-ru': 'Всегда на связи, выбирайте коллекцию',
    'es-es': 'Siempre dispuesto a elegir la colección',
    'fr-fr': 'Toujours invité à choisir la collection',
    'uk-ua': 'Завжди підкажуть вибрати колекцію',
    'de-ch': 'Immer zur Auswahl der Sammlung auffordern',
    'pt-br': 'Sempre solicitando a escolha da coleção',
  },
  treeEditor: {
    'en-us': 'Tree Editor',
    'ru-ru': 'Редактор дерева',
    'es-es': 'Editor de árboles',
    'fr-fr': "Éditeur d'arborescence",
    'uk-ua': 'Редактор дерева',
    'de-ch': 'Baumeditor',
    'pt-br': 'Editor de Árvores',
  },
  treeAccentColor: {
    'en-us': 'Tree accent color',
    'ru-ru': 'Цвет акцента дерева',
    'es-es': 'Color de acento del árbol',
    'fr-fr': "Couleur d'accent d'arbre",
    'uk-ua': 'Колір акценту дерева',
    'de-ch': 'Baumakzentfarbe',
    'pt-br': 'cor de destaque da árvore',
  },
  synonymColor: {
    'en-us': 'Synonym color',
    'ru-ru': 'Синоним цвета',
    'es-es': 'Color sinónimo',
    'fr-fr': 'Synonyme couleur',
    'uk-ua': 'Синонім кольору',
    'de-ch': 'Synonymfarbe',
    'pt-br': 'Cor (Sinônimo)',
  },
  showNewDataSetWarning: {
    'en-us': 'Show new Data Set warning',
    'ru-ru': 'Показать новое предупреждение о наборе данных',
    'es-es': 'Mostrar nueva advertencia de conjunto de datos',
    'fr-fr': "Afficher un nouvel avertissement sur l'ensemble de données",
    'uk-ua': 'Показати попередження про новий набір даних',
    'de-ch': 'Warnung für neuen Datensatz anzeigen',
    'pt-br': 'Exibir aviso de novo conjunto de dados',
  },
  showNewDataSetWarningDescription: {
    'en-us': 'Show an informational message when creating a new Data Set.',
    'ru-ru':
      'При создании нового набора данных отображать информационное сообщение.',
    'es-es':
      'Mostrar un mensaje informativo al crear un nuevo conjunto de datos.',
    'fr-fr':
      "Afficher un message d'information lors de la création d'un nouvel ensemble de données.",
    'uk-ua':
      'Показувати інформаційне повідомлення під час створення нового набору даних.',
    'de-ch': 'Zeige eine Meldung beim erstellen eines neuen Datensatzes an.',
    'pt-br':
      'Exibir uma mensagem informativa ao criar um novo conjunto de dados.',
  },
  header: {
    'en-us': 'Navigation Menu',
    'ru-ru': 'Навигационное меню',
    'es-es': 'Menú de navegación',
    'fr-fr': 'le menu de navigation',
    'uk-ua': 'Навігаційне меню',
    'de-ch': 'Navigationsmenü',
    'pt-br': 'Menu de navegação',
  },
  application: {
    'en-us': 'Application',
    'ru-ru': 'Приложение',
    'es-es': 'Solicitud',
    'fr-fr': 'Application',
    'uk-ua': 'застосування',
    'de-ch': 'Anwendung',
    'pt-br': 'Aplicativo',
  },
  allowDismissingErrors: {
    'en-us': 'Allow dismissing error messages',
    'ru-ru': 'Разрешить закрытие сообщений об ошибках',
    'es-es': 'Permitir descartar mensajes de error',
    'fr-fr': "Autoriser le rejet des messages d'erreur",
    'uk-ua': 'Дозволити закривати повідомлення про помилки',
    'de-ch': 'Erlaube das Verwerfen von Fehlermeldungen',
    'pt-br': 'Permitir descartar mensagens de erro',
  },
  updatePageTitle: {
    'en-us': 'Update page title',
    'ru-ru': 'Обновить заголовок страницы',
    'es-es': 'Actualizar el título de la página',
    'fr-fr': 'Mettre à jour le titre de la page',
    'uk-ua': 'Оновити назву сторінки',
    'de-ch': 'Seitentitel aktualisieren',
    'pt-br': 'Atualizar título da página',
  },
  updatePageTitleDescription: {
    'en-us':
      "Whether to update the title of the page to match dialog's header.",
    'ru-ru':
      'Следует ли обновлять заголовок страницы в соответствии с заголовком диалогового окна.',
    'es-es':
      'Si se debe actualizar el título de la página para que coincida con el encabezado del cuadro de diálogo.',
    'fr-fr':
      "S'il faut mettre à jour le titre de la page pour qu'il corresponde à l'en-tête de la boîte de dialogue.",
    'uk-ua':
      'Чи оновлювати назву сторінки відповідно до заголовка діалогового вікна.',
    'de-ch':
      'Titel der Seite so aktualisieren, dass er mit der Kopfzeile des Dialogs übereinstimmt.',
    'pt-br':
      'Se o título da página deve ser atualizado para corresponder ao cabeçalho da caixa de diálogo.',
  },
  updatePageTitleFormDescription: {
    'en-us': 'Whether to update the title of the page to match current record.',
    'ru-ru':
      'Следует ли обновить заголовок страницы в соответствии с текущей записью.',
    'es-es':
      'Si desea actualizar el título de la página para que coincida con el registro actual.',
    'fr-fr':
      "S'il faut mettre à jour le titre de la page pour qu'il corresponde à l'enregistrement actuel.",
    'uk-ua': 'Чи оновлювати назву сторінки відповідно до поточного запису.',
    'de-ch':
      'Titel der Seite aktualisieren, damit er mit dem aktuellen Datensatz übereinstimmt.',
    'pt-br':
      'Se o título da página deve ser atualizado para corresponder ao registro atual.',
  },
  queryComboBox: {
    'en-us': 'Query Combo Box',
    'ru-ru': 'Выпадающий список запросов',
    'es-es': 'Cuadro combinado de consulta',
    'uk-ua': 'Поле зі списком запитів',
    'de-ch': 'Abfrage-Kombinationsfeld',
    'fr-fr': 'Zone de liste déroulante de requête',
    'pt-br': 'Caixa de combinação de consulta',
  },
  searchAlgorithm: {
    'en-us': 'Search Algorithm',
    'ru-ru': 'Алгоритм поиска',
    'es-es': 'Algoritmo de búsqueda',
    'fr-fr': 'Algorithme de recherche',
    'uk-ua': 'Алгоритм пошуку',
    'de-ch': 'Suchalgorithmus',
    'pt-br': 'Algoritmo de Busca',
  },
  treeSearchAlgorithm: {
    'en-us': 'Search Algorithm (for relationships with tree tables)',
    'ru-ru':
      'Алгоритм поиска (для установления связей с помощью древовидных таблиц)',
    'es-es': 'Algoritmo de búsqueda (para relaciones con tablas de árboles)',
    'fr-fr':
      'Algorithme de recherche (pour les relations avec les tables arborescentes)',
    'uk-ua': 'Алгоритм пошуку (для зв’язків із деревоподібними таблицями)',
    'de-ch': 'Suchalgorithmus (für Beziehungen mit Baumtabellen)',
    'pt-br': 'Algoritmo de busca (para relacionamentos com tabelas em árvore)',
  },
  startsWithInsensitive: {
    'en-us': 'Starts With (case-insensitive)',
    'ru-ru': 'Начинается с (регистр не учитывается)',
    'es-es': 'Comienza con (sin distinguir entre mayúsculas y minúsculas)',
    'fr-fr': 'Commence par (insensible à la casse)',
    'uk-ua': 'Починається з (без урахування регістру)',
    'de-ch': 'Beginnt mit (Groß-/Kleinschreibung wird nicht beachtet)',
    'pt-br': 'Começa com (sem distinção entre maiúsculas e minúsculas)',
  },
  startsWithDescription: {
    'en-us': 'Search for values that begin with a given query string.',
    'ru-ru': 'Найдите значения, начинающиеся с заданной поисковой строки.',
    'es-es':
      'Busque valores que comiencen con una cadena de consulta determinada.',
    'fr-fr':
      'Rechercher des valeurs commençant par une chaîne de requête donnée.',
    'uk-ua': 'Пошук значень, які починаються з заданого рядка запиту.',
    'de-ch':
      'Suche nach Werten, die mit einer bestimmten Suchanfrage beginnen.',
    'pt-br':
      'Pesquise valores que comecem com uma determinada sequência de consulta.',
  },
  startsWithCaseSensitive: {
    'en-us': 'Starts With (case-sensitive)',
    'ru-ru': 'Начинается с (регистр имеет значение)',
    'es-es': 'Comienza con (sensible a mayúsculas y minúsculas)',
    'fr-fr': 'Commence par (sensible à la casse)',
    'uk-ua': 'Починається з (з урахуванням регістру)',
    'de-ch': 'Beginnt mit (Groß-/Kleinschreibung beachten)',
    'pt-br': 'Começa com (diferencia maiúsculas de minúsculas)',
  },
  startsWithCaseSensitiveDescription: {
    'en-us': 'Search for values that begin with a given query string.',
    'ru-ru': 'Найдите значения, начинающиеся с заданной поисковой строки.',
    'es-es':
      'Busque valores que comiencen con una cadena de consulta determinada.',
    'fr-fr':
      'Recherchez les valeurs qui commencent par une chaîne de requête donnée.',
    'uk-ua': 'Пошук значень, які починаються з заданого рядка запиту.',
    'de-ch':
      'Suche nach Werten, die mit einer bestimmten Suchanfrage beginnen.',
    'pt-br':
      'Pesquise valores que comecem com uma determinada sequência de consulta.',
  },
  containsInsensitive: {
    'en-us': 'Contains (case-insensitive)',
    'ru-ru': 'Содержит (регистр нечувствителен)',
    'es-es': 'Contiene (sin distinguir entre mayúsculas y minúsculas)',
    'fr-fr': 'Contient (insensible à la casse)',
    'uk-ua': 'Містить (незалежно від регістру)',
    'de-ch': 'Enthält (Groß-/Kleinschreibung wird nicht beachtet)',
    'pt-br': 'Contém (sem distinção entre maiúsculas e minúsculas)',
  },
  containsCaseSensitive: {
    'en-us': 'Contains (case-sensitive)',
    'ru-ru': 'Содержит (с учетом регистра)',
    'es-es': 'Contiene (sensible a mayúsculas y minúsculas)',
    'fr-fr': 'Contient (sensible à la casse)',
    'uk-ua': 'Містить (з урахуванням регістру)',
    'de-ch': 'Enthält (Groß-/Kleinschreibung beachten)',
    'pt-br': 'Contém (diferencia maiúsculas de minúsculas)',
  },
  containsDescription: {
    'en-us':
      'Search for values that contain a given query string (case-insensitive).',
    'ru-ru':
      'Поиск значений, содержащих заданную строку запроса (регистр не учитывается).',
    'es-es':
      'Busque valores que contengan una cadena de consulta determinada (sin distinguir entre mayúsculas y minúsculas).',
    'uk-ua':
      'Пошук значень, які містять заданий рядок запиту (незалежно від регістру).',
    'de-ch':
      'Suche nach Werten, die eine bestimmte Suchanfrage enthalten (Groß-/Kleinschreibung wird nicht beachtet).',
    'fr-fr':
      'Recherchez les valeurs contenant une chaîne de requête donnée (insensible à la casse).',
    'pt-br':
      'Pesquise valores que contenham uma determinada string de consulta (sem distinção entre maiúsculas e minúsculas).',
  },
  containsCaseSensitiveDescription: {
    'en-us':
      'Search for values that contain a given query string (case-sensitive).',
    'ru-ru':
      'Поиск значений, содержащих заданную строку запроса (с учетом регистра).',
    'es-es':
      'Busque valores que contengan una cadena de consulta determinada (distingue entre mayúsculas y minúsculas).',
    'fr-fr':
      'Recherchez les valeurs contenant une chaîne de requête donnée (sensible à la casse).',
    'uk-ua':
      'Пошук значень, які містять заданий рядок запиту (з урахуванням регістру).',
    'de-ch':
      'Suche nach Werten, die eine bestimmte Suchanfrage enthalten (Groß-/Kleinschreibung beachten).',
    'pt-br':
      'Pesquise valores que contenham uma determinada string de consulta (diferenciando maiúsculas de minúsculas).',
  },
  containsSecondDescription: {
    'en-us':
      'Can use _ to match any single character or % to match any number of characters.',
    'ru-ru':
      'Символ _ можно использовать для сопоставления одного символа, а символ % — для сопоставления любого количества символов.',
    'es-es':
      'Puede utilizar _ para que coincida con cualquier carácter individual o % para que coincida con cualquier número de caracteres.',
    'fr-fr':
      "Peut utiliser _ pour correspondre à n'importe quel caractère ou % pour correspondre à n'importe quel nombre de caractères.",
    'uk-ua':
      'Можна використовувати _ для відповідності будь-якому одному символу або % для відповідності будь-якій кількості символів.',
    'de-ch':
      'Mit _ kann man ein einzelnes Zeichen finden, mit % eine beliebige Anzahl von Zeichen.',
    'pt-br':
      'Pode-se usar _ para corresponder a qualquer caractere único ou % para corresponder a qualquer número de caracteres.',
  },
  highlightMatch: {
    'en-us': 'Highlight matched substring',
    'ru-ru': 'Выделить найденную подстроку',
    'es-es': 'Resaltar la subcadena coincidente',
    'fr-fr': 'Mettre en surbrillance la sous-chaîne correspondante',
    'uk-ua': 'Виділіть збіг підрядка',
    'de-ch': 'Übereinstimmende Teilzeichenkette hervorheben',
    'pt-br': 'Destacar a substring correspondente',
  },
  languageDescription: {
    'en-us': 'Determines field captions, usage notes and table captions.',
    'ru-ru':
      'Определяет заголовки полей, примечания по использованию и заголовки таблиц.',
    'es-es': 'Determina títulos de campos, notas de uso y títulos de tablas.',
    'fr-fr':
      "Détermine les légendes des champs, les notes d'utilisation et les légendes des tableaux.",
    'uk-ua':
      'Визначає підписи полів, примітки щодо використання та підписи таблиць.',
    'de-ch':
      'Legt Feldbezeichnungen, Nutzungshinweise und Tabellenbezeichnungen fest.',
    'pt-br':
      'Determina legendas de campos, notas de uso e legendas de tabelas.',
  },
  showDialogIcon: {
    'en-us': 'Show icon in the header',
    'ru-ru': 'Показать значок в заголовке',
    'es-es': 'Mostrar icono en el encabezado',
    'fr-fr': "Afficher l'icône dans l'en-tête",
    'uk-ua': 'Показати значок у заголовку',
    'de-ch': 'Symbol in der Kopfzeile anzeigen',
    'pt-br': 'Exibir ícone no cabeçalho',
  },
  scaleInterface: {
    'en-us': 'Scale Interface',
    'ru-ru': 'Масштабирование интерфейса',
    'es-es': 'Interfaz de escala',
    'fr-fr': 'Interface de balance',
    'uk-ua': 'Інтерфейс масштабу',
    'de-ch': 'Skalenschnittstelle',
    'pt-br': 'Interface de escala',
  },
  scaleInterfaceDescription: {
    'en-us': 'Scale interface to match font size.',
    'ru-ru': 'Масштабируйте интерфейс в соответствии с размером шрифта.',
    'es-es': 'Escala la interfaz para que coincida con el tamaño de la fuente.',
    'fr-fr': "Adapter l'interface à la taille de la police.",
    'uk-ua': 'Масштабуйте інтерфейс відповідно до розміру шрифту.',
    'de-ch': 'Skalieren Sie die Benutzeroberfläche an die Schriftgröße an.',
    'pt-br': 'Ajuste a interface para corresponder ao tamanho da fonte.',
  },
  displayAuthor: {
    'en-us': 'Show author in the tree',
    'ru-ru': 'Показать автора в дереве',
    'es-es': 'Mostrar autor en el árbol',
    'fr-fr': "Afficher l'auteur dans l'arbre",
    'uk-ua': 'Показати автора в дереві',
    'de-ch': 'Autor im Baum anzeigen',
    'pt-br': 'Mostrar autor',
  },
  welcomePage: {
    'en-us': 'Home Page',
    'ru-ru': 'Главная страница',
    'es-es': 'Página de inicio',
    'fr-fr': "Page d'accueil",
    'uk-ua': 'Домашня сторінка',
    'de-ch': 'Startseite',
    'pt-br': 'Página inicial',
  },
  content: {
    'en-us': 'Content',
    'ru-ru': 'Содержание',
    'es-es': 'Contenido',
    'fr-fr': 'Contenu',
    'uk-ua': 'Зміст',
    'de-ch': 'Inhalt',
    'pt-br': 'Contente',
  },
  defaultImage: {
    'en-us': 'Specify Splash',
    'ru-ru': 'Укажите логотип',
    'es-es': 'Especificar logotipo',
    'fr-fr': 'Spécifier le logo',
    'uk-ua': 'Вкажіть логотип',
    'de-ch': 'Logo angeben',
    'pt-br': 'Especificar logotipo',
  },
  critterless: {
    'en-us': 'Specify Splash (No Critters)',
    'ru-ru': 'Без существ',
    'es-es': 'Sin criaturas',
    'fr-fr': 'Sans créatures',
    'uk-ua': 'Без створінь',
    'de-ch': 'Kritterlos',
    'pt-br': 'Sem criaturas',
  },
  customImage: {
    'en-us': 'Custom Image',
    'ru-ru': 'Пользовательское изображение',
    'es-es': 'Imagen personalizada',
    'fr-fr': 'Image personnalisée',
    'uk-ua': 'Спеціальне зображення',
    'de-ch': 'Benutzerdefiniertes Bild',
    'pt-br': 'Imagem personalizada',
  },
  embeddedWebpage: {
    'en-us': 'Embedded web page',
    'ru-ru': 'Встроенная веб-страница',
    'es-es': 'Página web incrustada',
    'fr-fr': 'Page Web intégrée',
    'uk-ua': 'Вбудована веб-сторінка',
    'de-ch': 'Eingebettete Webseite',
    'pt-br': 'Página web incorporada',
  },
  embeddedWebpageDescription: {
    'en-us': 'A URL to a page that would be embedded on the home page:',
    'ru-ru': 'URL-адрес страницы, которая будет встроена на главную страницу:',
    'es-es': 'Una URL a una página que se integrará en la página de inicio:',
    'fr-fr': "Une URL vers une page qui serait intégrée à la page d'accueil :",
    'uk-ua': 'URL-адреса сторінки, яка буде вбудована на домашній сторінці:',
    'de-ch':
      'Eine URL zu einer Seite, die auf der Startseite eingebettet werden soll:',
    'pt-br': 'Um URL para uma página que seria incorporada na página inicial:',
  },
  behavior: {
    'en-us': 'Behavior',
    'ru-ru': 'Поведение',
    'es-es': 'Comportamiento',
    'fr-fr': 'Comportement',
    'uk-ua': 'Поведінка',
    'de-ch': 'Verhalten',
    'pt-br': 'Comportamento',
  },
  inheritance: {
    'en-us': 'Inheritance',
    'de-ch': 'Nachlass',
    'es-es': 'Herencia',
    'fr-fr': 'Héritage',
    'pt-br': 'Herança',
    'ru-ru': 'Наследование',
    'uk-ua': 'Спадщина',
  },
  noRestrictionsMode: {
    'en-us': 'No restrictions mode',
    'ru-ru': 'Режим без ограничений',
    'es-es': 'Modo sin restricciones',
    'fr-fr': 'Mode sans restriction',
    'uk-ua': 'Режим без обмежень',
    'de-ch': 'Modus ohne Einschränkungen',
    'pt-br': 'Modo sem restrições',
  },
  noRestrictionsModeWbDescription: {
    'en-us': 'Allows uploading data to any field in any table.',
    'ru-ru': 'Позволяет загружать данные в любое поле любой таблицы.',
    'es-es': 'Permite cargar datos a cualquier campo de cualquier tabla.',
    'fr-fr':
      "Permet de télécharger des données dans n'importe quel champ de n'importe quelle table.",
    'uk-ua': 'Дозволяє завантажувати дані в будь-яке поле будь-якої таблиці.',
    'de-ch':
      'Ermöglicht das Hochladen von Daten in beliebige Felder beliebiger Tabellen.',
    'pt-br':
      'Permite o carregamento de dados para qualquer campo em qualquer tabela.',
  },
  noRestrictionsModeQueryDescription: {
    'en-us': 'Allows querying data from any field in any table.',
    'ru-ru': 'Позволяет запрашивать данные из любого поля любой таблицы.',
    'es-es': 'Permite consultar datos de cualquier campo de cualquier tabla.',
    'fr-fr':
      "Permet d'interroger les données de n'importe quel champ de n'importe quelle table.",
    'uk-ua': 'Дозволяє запитувати дані з будь-якого поля будь-якої таблиці.',
    'de-ch':
      'Ermöglicht das Abfragen von Daten aus beliebigen Feldern in beliebigen Tabellen.',
    'pt-br': 'Permite consultar dados de qualquer campo em qualquer tabela.',
  },
  noRestrictionsModeWarning: {
    'en-us':
      'WARNING: enabling this may lead to data loss or database corruption. Please make sure you know what you are doing.',
    'ru-ru':
      'ВНИМАНИЕ: включение этой функции может привести к потере данных или повреждению базы данных. Пожалуйста, убедитесь, что вы понимаете, что делаете.',
    'es-es':
      'ADVERTENCIA: Habilitar esta opción podría provocar la pérdida de datos o la corrupción de la base de datos. Asegúrese de saber lo que está haciendo.',
    'uk-ua':
      'ПОПЕРЕДЖЕННЯ: увімкнення цієї функції може призвести до втрати даних або пошкодження бази даних. Переконайтеся, що ви знаєте, що робите.',
    'de-ch':
      'WARNUNG: Die Aktivierung dieser Funktion kann zu Datenverlust oder Beschädigung der Datenbank führen. Bitte stellen Sie sicher, dass Sie wissen, was Sie tun.',
    'fr-fr':
      "AVERTISSEMENT : l'activation de cette option peut entraîner une perte de données ou une corruption de la base de données. Veuillez vous assurer que vous savez ce que vous faites.",
    'pt-br':
      'AVISO: habilitar esta opção pode levar à perda de dados ou corrupção do banco de dados. Certifique-se de saber o que está fazendo.',
  },
  adminsOnlyPreference: {
    'en-us': "You don't have permission to change this option",
    'ru-ru': 'У вас нет разрешения на изменение этой опции.',
    'es-es': 'No tienes permiso para cambiar esta opción',
    'fr-fr': "Vous n'êtes pas autorisé à modifier cette option",
    'uk-ua': 'Ви не маєте дозволу змінювати цей параметр',
    'de-ch': 'Sie haben keine Berechtigung, diese Option zu ändern.',
    'pt-br': 'Você não tem permissão para alterar esta opção.',
  },
  stickyScrolling: {
    'en-us': 'Sticky scroll bar',
    'ru-ru': 'Залипающая полоса прокрутки',
    'es-es': 'Barra de desplazamiento fija',
    'fr-fr': 'Barre de défilement collante',
    'uk-ua': 'Липка смуга прокрутки',
    'de-ch': 'Sticky Scroll Bar',
    'pt-br': 'barra de rolagem fixa',
  },
  foreground: {
    'en-us': 'Foreground',
    'ru-ru': 'Передний план',
    'es-es': 'Primer plano',
    'fr-fr': 'Premier plan',
    'uk-ua': 'Передній план',
    'de-ch': 'Vordergrund',
    'pt-br': 'Primeiro plano',
  },
  background: {
    'en-us': 'Background',
    'ru-ru': 'Фон',
    'es-es': 'Fondo',
    'fr-fr': 'Arrière-plan',
    'uk-ua': 'Фон',
    'de-ch': 'Hintergrund',
    'pt-br': 'Fundo',
  },
  sidebarTheme: {
    'en-us': 'Sidebar theme',
    'de-ch': 'Seitenleisten-Design',
    'es-es': 'Tema de la barra lateral',
    'fr-fr': 'Thème de la barre latérale',
    'ru-ru': 'Тема боковой панели',
    'uk-ua': 'Тема бічної панелі',
    'pt-br': 'Tema da barra lateral',
  },
  darkForeground: {
    'en-us': 'Foreground (dark theme)',
    'ru-ru': 'Передний план (темная тема)',
    'es-es': 'Primer plano (tema oscuro)',
    'fr-fr': 'Premier plan (thème sombre)',
    'uk-ua': 'Передній план (темна тема)',
    'de-ch': 'Vordergrund (dunkles Design)',
    'pt-br': 'Primeiro plano (tema escuro)',
  },
  darkBackground: {
    'en-us': 'Background (dark theme)',
    'ru-ru': 'Фон (темная тема)',
    'es-es': 'Fondo (tema oscuro)',
    'fr-fr': 'Arrière-plan (thème sombre)',
    'uk-ua': 'Фон (темна тема)',
    'de-ch': 'Hintergrund (dunkles Design)',
    'pt-br': 'Plano de fundo (tema escuro)',
  },
  accentColor1: {
    'en-us': 'Accent color 1',
    'ru-ru': 'Дополнительный цвет 1',
    'es-es': 'Color de acento 1',
    'fr-fr': "Couleur d'accent 1",
    'uk-ua': 'Акцентний колір 1',
    'de-ch': 'Akzentfarbe 1',
    'pt-br': 'Cor de destaque 1',
  },
  accentColor2: {
    'en-us': 'Accent color 2',
    'ru-ru': 'Акцентный цвет 2',
    'es-es': 'Color de acento 2',
    'fr-fr': "Couleur d'accent 2",
    'uk-ua': 'Акцентний колір 2',
    'de-ch': 'Akzentfarbe 2',
    'pt-br': 'Cor de destaque 2',
  },
  accentColor3: {
    'en-us': 'Accent color 3',
    'ru-ru': 'Акцентный цвет 3',
    'es-es': 'Color de acento 3',
    'fr-fr': "Couleur d'accent 3",
    'uk-ua': 'Акцентний колір 3',
    'de-ch': 'Akzentfarbe 3',
    'pt-br': 'Cor de destaque 3',
  },
  accentColor4: {
    'en-us': 'Accent color 4',
    'ru-ru': 'Дополнительный цвет 4',
    'es-es': 'Color de acento 4',
    'fr-fr': "Couleur d'accent 4",
    'uk-ua': 'Акцентний колір 4',
    'de-ch': 'Akzentfarbe 4',
    'pt-br': 'Cor de destaque 4',
  },
  spreadsheet: {
    'en-us': 'Spreadsheet',
    'ru-ru': 'Электронная таблица',
    'es-es': 'Hoja de cálculo',
    'fr-fr': 'Tableur',
    'uk-ua': 'Електронна таблиця',
    'de-ch': 'Kalkulationstabelle',
    'pt-br': 'Planilha',
  },
  minSpareRows: {
    'en-us': 'Number of blank rows at the end',
    'ru-ru': 'Количество пустых строк в конце',
    'es-es': 'Número de filas en blanco al final',
    'fr-fr': 'Nombre de lignes vides à la fin',
    'uk-ua': 'Кількість порожніх рядків у кінці',
    'de-ch': 'Anzahl der leeren Zeilen am Ende',
    'pt-br': 'Número de linhas em branco no final',
  },
  autoWrapCols: {
    'en-us': 'Navigate to the other side when reaching the edge column',
    'ru-ru': 'Когда дойдете до краевого столбца, перейдите на другую сторону.',
    'es-es': 'Navegue hacia el otro lado al llegar a la columna del borde.',
    'fr-fr':
      'Naviguez de l’autre côté lorsque vous atteignez la colonne de bord',
    'uk-ua': 'Перейдіть на іншу сторону, коли досягнете краю колонки',
    'de-ch':
      'Wechseln Sie zur anderen Seite, sobald Sie die Randspalte erreicht haben.',
    'pt-br': 'Navegue até o outro lado ao chegar à coluna da borda.',
  },
  autoWrapRows: {
    'en-us': 'Navigate to the other side when reaching the edge row',
    'ru-ru': 'Когда дойдете до крайнего ряда, перейдите на другую сторону.',
    'es-es': 'Navegue hacia el otro lado al llegar a la fila del borde.',
    'fr-fr':
      'Naviguez de l’autre côté lorsque vous atteignez la rangée de bord',
    'uk-ua': 'Перейдіть на іншу сторону, коли досягнете крайнього ряду',
    'de-ch':
      'Wechseln Sie zur anderen Seite, sobald Sie die Randreihe erreicht haben.',
    'pt-br': 'Navegue até o outro lado ao chegar à fileira da borda.',
  },
  enterBeginsEditing: {
    'en-us': 'Enter key begins editing cell',
    'ru-ru': 'Нажатие клавиши Enter начинает редактирование ячейки',
    'es-es': 'La tecla Enter inicia la edición de la celda',
    'fr-fr': 'La touche Entrée commence à modifier la cellule',
    'uk-ua': 'Клавіша Enter починає редагування клітинки',
    'de-ch': 'Die Eingabetaste startet die Bearbeitung der Zelle.',
    'pt-br': 'A tecla Enter inicia a edição da célula.',
  },
  tabMoveDirection: {
    'en-us': 'Direction of movement when <key>Tab</key> key is pressed',
    'ru-ru': 'Направление движения при нажатии клавиши <key>Tab</key>',
    'es-es':
      'Dirección de movimiento cuando se presiona la tecla <key>Tab</key>',
    'fr-fr':
      'Sens de déplacement lorsque la touche <key>Tabulation</key> est enfoncée',
    'uk-ua': 'Напрямок руху при натисканні клавіші <key>Tab</key>',
    'de-ch': 'Bewegungsrichtung bei Drücken der Tabulatortaste <key></key>.',
    'pt-br': 'Direção do movimento quando a tecla <key>Tab</key> é pressionada',
  },
  tabMoveDirectionDescription: {
    'en-us':
      'You can move in the opposite direction by pressing <key>Shift</key>+<key>Tab</key>.',
    'ru-ru':
      'Для перемещения в противоположном направлении нажмите <key>Shift</key>+<key>Tab</key>.',
    'es-es':
      'Puedes moverte en la dirección opuesta presionando <key>Shift</key>+<key>Tab</key>.',
    'fr-fr':
      'Vous pouvez vous déplacer dans la direction opposée en appuyant sur <key>Shift</key>+<key>Tab</key>.',
    'uk-ua':
      'Ви можете рухатися в протилежному напрямку, натискаючи <key>Shift</key>+<key>Tab</key>.',
    'de-ch':
      'Sie können in die entgegengesetzte Richtung wechseln, indem Sie <key>Shift</key>+<key>Tab</key> drücken.',
    'pt-br':
      'Você pode se mover na direção oposta pressionando <key>Shift</key>+<key>Tab</key>.',
  },
  column: {
    'en-us': 'Column',
    'ru-ru': 'Столбец',
    'es-es': 'Columna',
    'fr-fr': 'Colonne',
    'uk-ua': 'Колонка',
    'de-ch': 'Spalte',
    'pt-br': 'Coluna',
  },
  row: {
    'en-us': 'Row',
    'ru-ru': 'Ряд',
    'es-es': 'Fila',
    'fr-fr': 'Rangée',
    'uk-ua': 'рядок',
    'de-ch': 'Reihe',
    'pt-br': 'Linha',
  },
  enterMoveDirection: {
    'en-us': 'Direction of movement when <key>Enter</key> key is pressed',
    'ru-ru': 'Направление движения при нажатии клавиши <key>Enter</key>',
    'es-es':
      'Dirección de movimiento cuando se presiona la tecla <key>Enter</key>',
    'uk-ua': 'Напрямок руху, коли натиснуто клавішу <key>Enter</key>',
    'de-ch': 'Bewegungsrichtung beim Drücken der Taste <key>Enter</key>.',
    'fr-fr':
      'Direction du mouvement lorsque la touche <key>Entrer</key> est enfoncée',
    'pt-br':
      'Direção do movimento quando a tecla <key>Enter</key> é pressionada',
  },
  enterMoveDirectionDescription: {
    'en-us':
      'You can move in the opposite direction by pressing <key>Shift</key>+<key>Enter</key>.',
    'ru-ru':
      'Вы можете переместиться в противоположном направлении, нажав <key>Shift</key>+<key>Enter</key>.',
    'es-es':
      'Puedes moverte en la dirección opuesta presionando <key>Shift</key>+<key>Enter</key>.',
    'fr-fr': 'Synonyme couleur.',
    'uk-ua':
      'Ви можете рухатися у протилежному напрямку, натискаючи <key>Shift</key>+<key>Enter</key>.',
    'de-ch':
      'Sie können in die entgegengesetzte Richtung wechseln, indem Sie <key>Shift</key>+<key>Enter</key> drücken.',
    'pt-br':
      'Você pode se mover na direção oposta pressionando <key>Shift</key>+<key>Enter</key>.',
  },
  filterPickLists: {
    'en-us': 'Filter pick list items',
    'ru-ru': 'Фильтрация элементов выпадающего списка',
    'es-es': 'Filtrar elementos de la lista de selección',
    'fr-fr': 'Filtrer les éléments de la liste de sélection',
    'uk-ua': 'Фільтр вибору елементів списку',
    'de-ch': 'Filter-Auswahllistenelemente',
    'pt-br': 'Filtrar itens da lista de seleção',
  },
  exportFileDelimiter: {
    'en-us': 'Export file delimiter',
    'ru-ru': 'Разделитель экспортируемых файлов',
    'es-es': 'Delimitador de archivo de exportación',
    'fr-fr': "Délimiteur de fichier d'exportation",
    'uk-ua': 'Роздільник файлу експорту',
    'de-ch': 'Exportdatei-Trennzeichen',
    'pt-br': 'Delimitador de arquivo de exportação',
  },
  exportCsvUtf8Bom: {
    'en-us': 'Add UTF-8 BOM to CSV file exports',
    'ru-ru': 'Добавить UTF-8 BOM в экспорт CSV-файла',
    'es-es': 'Agregar BOM UTF-8 a las exportaciones de archivos CSV',
    'fr-fr': 'Ajouter UTF-8 BOM aux exportations de fichiers CSV',
    'uk-ua': 'Додайте специфікацію UTF-8 до експорту файлу CSVу',
    'de-ch': 'UTF-8 BOM zum CSV-Dateiexport hinzufügen',
    'pt-br': 'Adicionar BOM UTF-8 às exportações de arquivos CSV',
  },
  exportCsvUtf8BomDescription: {
    'en-us':
      'Adds a BOM (Byte Order Mark) to exported CSV files to ensure that the file is correctly recognized and displayed by various programs (Excel, OpenRefine, etc.), preventing issues with special characters and formatting.',
    'ru-ru': 'Корректное отображение экспортированных CSV-файлов в Excel.',
    'es-es':
      'Agrega una BOM (marca de orden de bytes) a los archivos CSV exportados para garantizar que el archivo sea reconocido y mostrado correctamente por varios programas (Excel, OpenRefine, etc.), evitando problemas con caracteres especiales y formato.',
    'fr-fr':
      "Permet aux exportations de fichiers CSV de s'afficher correctement dans Excel.",
    'uk-ua': 'Змушує експорт файлів CSV правильно відображатися в Excel.',
    'de-ch':
      'Sorgt dafür, dass CSV-Dateiexporte in Excel korrekt angezeigt werden.',
    'pt-br':
      'Adiciona uma BOM (Byte Order Mark) aos arquivos CSV exportados para garantir que o arquivo seja reconhecido e exibido corretamente por diversos programas (Excel, OpenRefine, etc.), evitando problemas com caracteres especiais e formatação.',
  },
  caseSensitive: {
    'en-us': 'Case-sensitive',
    'ru-ru': 'С учетом регистра',
    'es-es': 'Distingue mayúsculas y minúsculas',
    'fr-fr': 'Sensible aux majuscules et minuscules',
    'uk-ua': 'Чутливий до регістру',
    'de-ch': 'Groß-/Kleinschreibung beachten',
    'pt-br': 'Maiúsculas e minúsculas',
  },
  caseInsensitive: {
    'en-us': 'Case-insensitive',
    'ru-ru': 'регистронечувствительный',
    'es-es': 'No distingue entre mayúsculas y minúsculas',
    'fr-fr': 'Insensible à la casse',
    'uk-ua': 'Регістр не враховується',
    'de-ch': 'Groß-/Kleinschreibung wird nicht beachtet',
    'pt-br': 'Sem distinção entre maiúsculas e minúsculas',
  },
  showNoReadTables: {
    'en-us': 'Show tables without "Read" access',
    'ru-ru': 'Показать таблицы без доступа на чтение.',
    'es-es': 'Mostrar tablas sin acceso de "Lectura"',
    'fr-fr': 'Afficher les tableaux sans accès "Lecture"',
    'uk-ua': 'Показувати таблиці без доступу «Читання»',
    'de-ch': 'Tabellen ohne Lesezugriff anzeigen',
    'pt-br': 'Exibir tabelas sem acesso de "Leitura"',
  },
  showNoAccessTables: {
    'en-us': 'Show tables without "Create" access',
    'ru-ru': 'Показать таблицы без доступа "Создать".',
    'es-es': 'Mostrar tablas sin acceso "Crear"',
    'fr-fr': 'Afficher les tableaux sans accès "Créer"',
    'uk-ua': 'Показувати таблиці без доступу «Створити»',
    'de-ch': 'Tabellen ohne "Erstellen"-Zugriff anzeigen',
    'pt-br': 'Exibir tabelas sem permissão de "Criar"',
  },
  textAreaAutoGrow: {
    'en-us': 'Text boxes grow automatically',
    'ru-ru': 'Текстовые поля автоматически увеличиваются в размере.',
    'es-es': 'Los cuadros de texto crecen automáticamente',
    'fr-fr': "Les zones de texte s'agrandissent automatiquement",
    'uk-ua': 'Текстові поля збільшуються автоматично',
    'de-ch': 'Textfelder vergrößern sich automatisch',
    'pt-br': 'As caixas de texto aumentam de tamanho automaticamente.',
  },
  clearQueryFilters: {
    'en-us': 'Reset query filters',
    'ru-ru': 'Сбросить фильтры запроса',
    'es-es': 'Restablecer filtros de consulta',
    'fr-fr': 'Réinitialiser les filtres de requête',
    'uk-ua': 'Скинути фільтри запитів',
    'de-ch': 'Abfragefilter zurücksetzen',
    'pt-br': 'Redefinir filtros de consulta',
  },
  clearQueryFiltersDescription: {
    'en-us': 'Clears all query filters when running a Report from a Form.',
    'de-ch':
      'Löscht alle Abfragefilter beim Ausführen eines Berichts aus einem Formular.',
    'es-es':
      'Borra todos los filtros de consulta al ejecutar un informe desde un formulario.',
    'fr-fr':
      "Efface tous les filtres de requête lors de l'exécution d'un rapport à partir d'un formulaire.",
    'ru-ru': 'Сбрасывает все фильтры запроса при запуске отчета из формы.',
    'uk-ua': 'Очищає всі фільтри запитів під час запуску звіту з форми.',
    'pt-br':
      'Limpa todos os filtros de consulta ao executar um relatório a partir de um formulário.',
  },
  queryParamtersFromForm: {
    'en-us': 'Show query filters when running a Report from a Form',
    'de-ch':
      'Abfragefilter beim Ausführen eines Berichts aus einem Formular anzeigen',
    'es-es':
      'Mostrar filtros de consulta al ejecutar un informe desde un formulario',
    'fr-fr':
      "Afficher les filtres de requête lors de l'exécution d'un rapport à partir d'un formulaire",
    'ru-ru': 'Отображать фильтры запроса при создании отчета из формы',
    'uk-ua': 'Показувати фільтри запитів під час запуску звіту з форми',
    'pt-br':
      'Exibir filtros de consulta ao executar um relatório a partir de um formulário.',
  },
  autoGrowAutoComplete: {
    'en-us': 'Allow autocomplete to grow as wide as need',
    'ru-ru': 'Разрешите автозаполнению расширяться до необходимой ширины.',
    'es-es': 'Permitir que el autocompletado crezca tanto como sea necesario',
    'fr-fr':
      'Sens de déplacement lorsque la touche [X27X]Tabulation[X35X] est enfoncée',
    'uk-ua':
      'Дозволити автозаповнення розширюватися настільки, наскільки потрібно',
    'de-ch':
      'Ermöglichen Sie es der Autovervollständigung, sich so weit auszudehnen, wie nötig',
    'pt-br':
      'Permitir que o preenchimento automático se expanda o quanto for necessário.',
  },
  tableNameInTitle: {
    'en-us': 'Include table name in the browser page title',
    'ru-ru': 'Включите название таблицы в заголовок страницы в браузере.',
    'es-es':
      'Incluir el nombre de la tabla en el título de la página del navegador',
    'fr-fr':
      'Inclure le nom de la table dans le titre de la page du navigateur',
    'uk-ua': 'Включіть назву таблиці в заголовок сторінки браузера',
    'de-ch': 'Fügen Sie den Tabellennamen in den Seitentitel des Browsers ein.',
    'pt-br': 'Inclua o nome da tabela no título da página do navegador.',
  },
  focusFirstField: {
    'en-us': 'Focus first field',
    'de-ch': 'Fokus zuerst Feld',
    'es-es': 'Enfoque el primer campo',
    'fr-fr': 'Concentrez-vous sur le premier champ',
    'ru-ru': 'Сосредоточьтесь на первом поле',
    'uk-ua': 'Перейти до першого поля',
    'pt-br': 'Foque primeiro no campo',
  },
  doubleClickZoom: {
    'en-us': 'Double click to zoom',
    'ru-ru': 'Двойной щелчок для увеличения',
    'es-es': 'Haga doble clic para ampliar',
    'fr-fr': 'Double-cliquez pour zoomer',
    'uk-ua': 'Двічі клацніть, щоб збільшити',
    'de-ch': 'Doppelklicken zum Vergrößern',
    'pt-br': 'Clique duas vezes para ampliar.',
  },
  closePopupOnClick: {
    'en-us': 'Close pop-up on outside click',
    'ru-ru': 'Закрыть всплывающее окно при внешнем клике',
    'es-es': 'Cerrar ventana emergente al hacer clic desde fuera',
    'fr-fr': "Fermer la pop-up lors d'un clic extérieur",
    'uk-ua': 'Закрити спливаюче вікно при зовнішньому клацанні',
    'de-ch': 'Pop-up bei Klick von außen schließen',
    'pt-br': 'Fechar janela pop-up ao clicar fora dela',
  },
  animateTransitions: {
    'en-us': 'Animate transitions',
    'ru-ru': 'Анимированные переходы',
    'es-es': 'Animar transiciones',
    'fr-fr': 'Animer les transitions',
    'uk-ua': 'Анімація переходів',
    'de-ch': 'Übergänge animieren',
    'pt-br': 'Animar transições',
  },
  panInertia: {
    'en-us': 'Pan inertia',
    'ru-ru': 'пан инерция',
    'es-es': 'Inercia de la sartén',
    'fr-fr': 'Inertie du bac',
    'uk-ua': 'Інерція панорами',
    'de-ch': 'Pan-Trägheit',
    'pt-br': 'Inércia de Pan',
  },
  mouseDrags: {
    'en-us': 'Mouse drags',
    'ru-ru': 'Перетаскивание мышью',
    'es-es': 'El ratón arrastra',
    'uk-ua': 'Виділіть відповідний підрядок',
    'de-ch': 'Maus zieht',
    'fr-fr': 'Mettre en surbrillance la sous-chaîne correspondante',
    'pt-br': 'Arrastar o mouse',
  },
  scrollWheelZoom: {
    'en-us': 'Scroll wheel zoom',
    'ru-ru': 'масштабирование с помощью колесика мыши',
    'es-es': 'Zoom con rueda de desplazamiento',
    'fr-fr': 'Zoom avec la molette de défilement',
    'uk-ua': 'Масштаб колеса прокрутки',
    'de-ch': 'Zoomen mit dem Mausrad',
    'pt-br': 'zoom com a roda de rolagem',
  },
  flexibleColumnWidth: {
    'en-us': 'Flexible column width',
    'ru-ru': 'Гибкая ширина колонки',
    'es-es': 'Ancho de columna flexible',
    'fr-fr': 'Largeur de colonne flexible',
    'uk-ua': 'Гнучка ширина колонки',
    'de-ch': 'Flexible Spaltenbreite',
    'pt-br': 'Largura da coluna flexível',
  },
  flexibleSubGridColumnWidth: {
    'en-us': 'Flexible subview grid column width',
    'ru-ru': 'Гибкая ширина столбца сетки подпредставлений',
    'es-es': 'Ancho de columna de cuadrícula de subvista flexible',
    'fr-fr': 'Largeur de colonne flexible de la grille de sous-vue',
    'uk-ua': 'Гнучка ширина стовпця сітки вкладеного перегляду',
    'de-ch': 'Flexible Spaltenbreite des Unteransichtsrasters',
    'pt-br': 'Largura flexível da coluna da grade da subvisualização',
  },
  closeOnEsc: {
    'en-us': 'Close on <key>ESC</key> key press',
    'ru-ru': 'Закрыть при нажатии клавиши <key>ESC</key>',
    'es-es': 'Cerrar al presionar la tecla <key>ESC</key>',
    'fr-fr': 'Icône et nom de la table',
    'uk-ua': 'Закриття натисканням клавіші <key>ESC</key>',
    'de-ch': 'Schließen durch Drücken der Taste <key>ESC</key>.',
    'pt-br': 'Fechar ao pressionar a tecla <key>ESC</key>',
  },
  closeOnOutsideClick: {
    'en-us': 'Close on outside click',
    'ru-ru': 'Закрыть при внешнем щелчке',
    'es-es': 'Cerrar al hacer clic desde fuera',
    'fr-fr': 'Fermer sur clic extérieur',
    'uk-ua': 'Закрийте зовнішнім клацанням',
    'de-ch': 'Bei Klick von außen schließen',
    'pt-br': 'Fechar ao clicar externamente',
  },
  specifyNetworkBadge: {
    'en-us': 'Specify Network Badge',
    'ru-ru': 'Укажите сетевой значок',
    'es-es': 'Especificar la insignia de red',
    'fr-fr': 'Spécifiez le badge réseau',
    'uk-ua': 'Укажіть значок мережі',
    'de-ch': 'Netzwerkabzeichen angeben',
    'pt-br': 'Especificar o ícone de rede',
  },
  useAccessibleFullDatePicker: {
    'en-us': 'Use accessible full date picker',
    'ru-ru': 'Используйте удобный полнофункциональный инструмент выбора даты.',
    'es-es': 'Utilice el selector de fecha completo y accesible',
    'fr-fr': 'Utiliser un sélecteur de date complet accessible',
    'uk-ua': 'Використовуйте доступний повний засіб вибору дати',
    'de-ch': 'Verwenden Sie die barrierefreie Datumsauswahl.',
    'pt-br': 'Use o seletor de datas completo e acessível.',
  },
  useAccessibleMonthPicker: {
    'en-us': 'Use accessible month picker',
    'ru-ru': 'Используйте удобный инструмент выбора месяца.',
    'es-es': 'Utilice el selector de meses accesible',
    'fr-fr': 'Utiliser le sélecteur de mois accessible',
    'uk-ua': 'Використовуйте доступний засіб вибору місяця',
    'de-ch': 'Nutzen Sie die barrierefreie Monatsauswahl.',
    'pt-br': 'Use o seletor de mês acessível',
  },
  rightAlignNumberFields: {
    'en-us': 'Right-Justify numeric fields',
    'ru-ru': 'Выравнивание числовых полей по правому краю',
    'es-es': 'Justificar a la derecha los campos numéricos',
    'fr-fr': 'Justifier à droite les champs numériques',
    'uk-ua': 'Вирівнювання по правому краю числових полів',
    'de-ch': 'Numerische Felder rechtsbündig ausrichten',
    'pt-br': 'Justificar campos numéricos à direita',
  },
  roundedCorners: {
    'en-us': 'Rounded corners',
    'ru-ru': 'Закругленные углы',
    'es-es': 'esquinas redondeadas',
    'fr-fr': 'Coins arrondis',
    'uk-ua': 'Заокруглені кути',
    'de-ch': 'Abgerundete Ecken',
    'pt-br': 'Cantos arredondados',
  },
  showSubviewBorders: {
    'en-us': 'Show borders around subviews',
    'de-ch': 'Rahmen um Unteransichten anzeigen',
    'es-es': 'Mostrar bordes alrededor de las subvistas',
    'fr-fr': 'Afficher les bordures autour des sous-vues',
    'pt-br': 'Exibir bordas ao redor das subvisualizações',
    'ru-ru': 'Отобразить границы вокруг дочерних представлений',
    'uk-ua': 'Показати межі навколо підвидів',
  },
  limitMaxFieldWidth: {
    'en-us': 'Limit max field width',
    'ru-ru': 'Ограничить максимальную ширину поля',
    'es-es': 'Limitar el ancho máximo del campo',
    'fr-fr': 'Limiter la largeur maximale du champ',
    'uk-ua': 'Обмеження максимальної ширини поля',
    'de-ch': 'Maximale Feldbreite begrenzen',
    'pt-br': 'Limitar a largura máxima do campo',
  },
  condenseQueryResults: {
    'en-us': 'Condense query results',
    'ru-ru': 'Сжать результаты запроса',
    'es-es': 'Condensar los resultados de la consulta',
    'fr-fr': 'Condenser les résultats de la requête',
    'uk-ua': 'Згорнути результати запиту',
    'de-ch': 'Suchergebnisse zusammenfassen',
    'pt-br': 'Condensar resultados da consulta',
  },
  blurContentBehindDialog: {
    'en-us': 'Blur content behind the dialog',
    'ru-ru': 'Размыть содержимое за диалоговым окном',
    'es-es': 'Desenfocar el contenido detrás del diálogo',
    'fr-fr': 'Flou le contenu derrière la boîte de dialogue',
    'uk-ua': 'Розмити вміст за діалоговим вікном',
    'de-ch': 'Inhalte hinter dem Dialog verwischen',
    'pt-br': 'Desfocar o conteúdo atrás da caixa de diálogo',
  },
  collectionSortOrderDescription: {
    'en-us': 'This determines the visual order of collections.',
    'ru-ru': 'Это определяет визуальный порядок коллекций.',
    'es-es': 'Esto determina el orden visual de las colecciones.',
    'fr-fr': "Ceci détermine l'ordre visuel des collections.",
    'uk-ua': 'Це визначає візуальний порядок колекцій.',
    'de-ch': 'Dies bestimmt die visuelle Reihenfolge der Sammlungen.',
    'pt-br': 'Isso determina a ordem visual das coleções.',
  },
  recordSetRecordToOpen: {
    'en-us': 'Record to open by default',
    'ru-ru': 'Запись, которая будет открыта по умолчанию',
    'es-es': 'Registro para abrir por defecto',
    'fr-fr': 'Enregistrement à ouvrir par défaut',
    'uk-ua': 'Запис відкривається за умовчанням',
    'de-ch': 'Standardmäßig zu öffnender Datensatz',
    'pt-br': 'Gravar para abrir por padrão',
  },
  altClickToSupressNewTab: {
    'en-us':
      '<key>{altKeyName:string}</key>+<key>Click</key> to suppress new tab',
    'ru-ru':
      '<key>{altKeyName:string}</key>+<key>Нажмите</key>, чтобы скрыть новую вкладку',
    'es-es':
      '<key>{altKeyName:string}</key>+<key>Haga clic en </key> para suprimir la nueva pestaña',
    'fr-fr':
      '<key>{altKeyName:string}</key>+<key>Cliquez sur</key> pour supprimer le nouvel onglet',
    'uk-ua':
      '<key>{altKeyName:string}</key>+<key>Натисніть </key>, щоб закрити нову вкладку',
    'de-ch':
      '<key>{altKeyName:string}</key>+<key>Klicken Sie auf </key>, um einen neuen Tab zu unterdrücken',
    'pt-br':
      '<key>{altKeyName:string}</key>+<key>Clique</key> para fechar a nova aba',
  },
  altClickToSupressNewTabDescription: {
    'en-us':
      '<key>{altKeyName:string}</key>+<key>Click</key> a link that usually opens in a new tab to open it in the current tab.',
    'ru-ru':
      '<key>{altKeyName:string}</key>+<key>Щелкните</key> ссылку, которая обычно открывается в новой вкладке, чтобы открыть ее в текущей вкладке.',
    'es-es':
      '<key>{altKeyName:string}</key>+<key>Haga clic</key> en un enlace que normalmente se abre en una nueva pestaña para abrirlo en la pestaña actual.',
    'fr-fr': 'Utiliser le sélecteur de mois accessible.',
    'uk-ua':
      '<key>{altKeyName:string}</key>+<key>Натисніть</key> посилання, яке зазвичай відкривається в новій вкладці, щоб відкрити його в поточній вкладці.',
    'de-ch':
      '<key>{altKeyName:string}</key>+<key>Klicken Sie</key> auf einen Link, der normalerweise in einem neuen Tab geöffnet wird, um ihn im aktuellen Tab zu öffnen.',
    'pt-br':
      '<key>{altKeyName:string}</key>+<key>Clique</key> em um link que normalmente abre em uma nova guia para abri-lo na guia atual.',
  },
  makeFormDialogsModal: {
    'en-us': 'Make form dialogs gray out the background',
    'ru-ru': 'Сделайте так, чтобы диалоговые окна форм были затемнены.',
    'es-es':
      'Hacer que los cuadros de diálogo del formulario tengan el fondo en gris',
    'fr-fr':
      "Rendre les boîtes de dialogue de formulaire grisées sur l'arrière-plan",
    'uk-ua': 'Зробіть діалогові вікна форми сірими фоном',
    'de-ch': 'Formulardialoge sollen einen grauen Hintergrund haben',
    'pt-br':
      'Faça com que os diálogos de formulário deixem o fundo escurecido.',
  },
  autoScrollTree: {
    'en-us': 'Auto scroll tree to focused node',
    'ru-ru': 'Автоматическая прокрутка дерева к сфокусированному узлу',
    'es-es': 'Desplazamiento automático del árbol al nodo enfocado',
    'fr-fr': 'Arbre de défilement automatique vers le nœud ciblé',
    'uk-ua': 'Автоматичне прокручування дерева до виділеного вузла',
    'de-ch': 'Automatisches Scrollen des Baums zum fokussierten Knoten',
    'pt-br': 'A árvore de rolagem automática leva ao nó em foco.',
  },
  sortByField: {
    'en-us': 'Order By Field',
    'de-ch': 'Nach Feld sortieren',
    'es-es': 'Ordenar por campo',
    'fr-fr': 'Trier par champ',
    'pt-br': 'Ordenar por campo',
    'ru-ru': 'Сортировать по полю',
    'uk-ua': 'Сортувати за полем',
  },
  lineWrap: {
    'en-us': 'Line wrap',
    'ru-ru': 'перенос строки',
    'es-es': 'Ajuste de línea',
    'fr-fr': 'Retour à la ligne',
    'uk-ua': 'Обтікання лініями',
    'de-ch': 'Linienumwicklung',
    'pt-br': 'Enrolamento de linha',
  },
  indentSize: {
    'en-us': 'Indent size',
    'ru-ru': 'Размер отступа',
    'es-es': 'Tamaño de sangría',
    'fr-fr': 'Taille du retrait',
    'uk-ua': 'Розмір відступу',
    'de-ch': 'Einbuchtungsgröße',
    'pt-br': 'Tamanho do recuo',
  },
  indentWithTab: {
    'en-us': 'Indent with <key>Tab</key>',
    'ru-ru': 'Отступ с помощью <key>Tab</key>',
    'es-es': 'Sangría con <key>Tab</key>',
    'fr-fr': 'Indenter avec <key>Tabulation</key>',
    'uk-ua': 'Відступ із <key>Tab</key>',
    'de-ch': 'Einrücken mit <key>Tab</key>',
    'pt-br': 'Recuar com <key>Tab</key>',
  },
  formHeaderFormat: {
    'en-us': 'Form header format',
    'ru-ru': 'Формат заголовка формы',
    'es-es': 'Formato del encabezado del formulario',
    'fr-fr': "Format d'en-tête de formulaire",
    'uk-ua': 'Формат заголовка форми',
    'de-ch': 'Formularkopfformat',
    'pt-br': 'Formato do cabeçalho do formulário',
  },
  iconAndTableName: {
    'en-us': 'Icon and table name',
    'ru-ru': 'Значок и название таблицы',
    'es-es': 'Icono y nombre de la tabla',
    'fr-fr': 'Icône et nom de la table',
    'uk-ua': 'Значок і назва таблиці',
    'de-ch': 'Symbol- und Tabellenname',
    'pt-br': 'Ícone e nome da tabela',
  },
  tableIcon: {
    'en-us': 'Table icon',
    'ru-ru': 'значок таблицы',
    'es-es': 'Icono de tabla',
    'fr-fr': 'Icône de tableau',
    'uk-ua': 'Значок таблиці',
    'de-ch': 'Tischsymbol',
    'pt-br': 'Ícone de tabela',
  },
  maxHeight: {
    'en-us': 'Max height',
    'ru-ru': 'Максимальная высота',
    'es-es': 'Altura máxima',
    'fr-fr': 'hauteur maximum',
    'uk-ua': 'Максимальна висота',
    'de-ch': 'Maximale Höhe',
    'pt-br': 'Altura máxima',
  },
  autoComplete: {
    'en-us': 'Auto complete',
    'ru-ru': 'Автозаполнение',
    'es-es': 'Autocompletar',
    'fr-fr':
      "Détermine les légendes des champs, les notes d'utilisation et les légendes des tableaux",
    'uk-ua':
      'Визначає підписи полів, примітки щодо використання та підписи таблиць',
    'de-ch': 'Automatische Vervollständigung',
    'pt-br': 'Preenchimento automático',
  },
  searchCaseSensitive: {
    'en-us': 'Case-sensitive search',
    'es-es': 'Búsqueda que distingue entre mayúsculas y minúsculas',
    'fr-fr': 'Recherche sensible à la casse',
    'uk-ua': 'Пошук з урахуванням регістру',
    'de-ch': 'Suche mit Berücksichtigung der Groß- und Kleinschreibung',
    'ru-ru': 'Поиск с учетом регистра',
    'pt-br': 'Pesquisa que diferencia maiúsculas de minúsculas',
  },
  searchField: {
    'en-us': 'Search Field',
    'ru-ru': 'Поле поиска',
    'es-es': 'Campo de búsqueda',
    'fr-fr': 'Champ de recherche',
    'uk-ua': 'Поле пошуку',
    'de-ch': 'Suchfeld',
    'pt-br': 'Campo de pesquisa',
  },
  createInteractions: {
    'en-us': 'Creating an interaction',
    'ru-ru': 'Создание взаимодействия',
    'es-es': 'Creando una interacción',
    'fr-fr': 'Créer une interaction',
    'uk-ua': 'Створення взаємодії',
    'de-ch': 'Eine Interaktion erstellen',
    'pt-br': 'Criar uma interação',
  },
  tableIdentifier: {
    'en-us': '{tableName:string} identifier',
    'ru-ru': 'Идентификатор {tableName:string}',
    'es-es': 'Identificador de {tableName:string}',
    'fr-fr': 'Identifiant {tableName:string}',
    'uk-ua': 'Ідентифікатор {tableName:string}',
    'de-ch': '{tableName:string}-Identifikator',
    'pt-br': 'Identificador de {tableName:string}',
  },
  useSpaceAsDelimiter: {
    'en-us': 'Use space as delimiter',
    'ru-ru': 'Используйте пробел в качестве разделителя.',
    'es-es': 'Utilice el espacio como delimitador',
    'fr-fr': "Utiliser l'espace comme délimiteur",
    'uk-ua': 'Використовуйте пробіл як роздільник',
    'de-ch': 'Leerzeichen als Trennzeichen verwenden',
    'pt-br': 'Use o espaço como delimitador',
  },
  useCommaAsDelimiter: {
    'en-us': 'Use comma as delimiter',
    'ru-ru': 'Используйте запятую в качестве разделителя.',
    'es-es': 'Utilice la coma como delimitador',
    'fr-fr': 'Utiliser la virgule comme délimiteur',
    'uk-ua': 'Використовуйте кому як роздільник',
    'de-ch': 'Komma als Trennzeichen verwenden',
    'pt-br': 'Use vírgula como delimitador',
  },
  useNewLineAsDelimiter: {
    'en-us': 'Use new line as delimiter',
    'ru-ru': 'Используйте новую строку в качестве разделителя.',
    'es-es': 'Utilice nueva línea como delimitador',
    'fr-fr': 'Utiliser une nouvelle ligne comme délimiteur',
    'uk-ua': 'Використовуйте новий рядок як роздільник',
    'de-ch': 'Verwenden Sie einen Zeilenumbruch als Trennzeichen.',
    'pt-br': 'Usar nova linha como delimitador',
  },
  useCustomDelimiters: {
    'en-us': 'Use custom delimiters',
    'ru-ru': 'Используйте пользовательские разделители',
    'es-es': 'Utilice delimitadores personalizados',
    'fr-fr': 'Utiliser des délimiteurs personnalisés',
    'uk-ua': 'Використовуйте спеціальні роздільники',
    'de-ch': 'Benutzerdefinierte Trennzeichen verwenden',
    'pt-br': 'Use delimitadores personalizados',
  },
  useCustomDelimitersDescription: {
    'en-us':
      'A list of delimiters to use, in addition to the ones defined above. Put one delimiter per line.',
    'ru-ru':
      'Список разделителей, которые можно использовать в дополнение к указанным выше. Размещайте по одному разделителю на строку.',
    'es-es':
      'Una lista de delimitadores para usar, además de los definidos anteriormente. Coloque un delimitador por línea.',
    'fr-fr':
      'Une liste de délimiteurs à utiliser, en plus de ceux définis ci-dessus. Mettez un délimiteur par ligne.',
    'uk-ua':
      'Список розділювачів для використання на додаток до визначених вище. Поставте один роздільник на рядок.',
    'de-ch':
      'Eine Liste weiterer Trennzeichen, die zusätzlich zu den oben definierten verwendet werden können. Jedes Trennzeichen gehört in eine eigene Zeile.',
    'pt-br':
      'Uma lista de delimitadores a serem usados, além dos definidos acima. Coloque um delimitador por linha.',
  },
  detectAutomaticallyDescription: {
    'en-us': 'Detect automatically based on catalog number format.',
    'ru-ru': 'Автоматическое определение на основе формата каталожного номера.',
    'es-es':
      'Detectar automáticamente según el formato del número de catálogo.',
    'fr-fr':
      'Détecter automatiquement en fonction du format du numéro de catalogue.',
    'uk-ua': 'Визначати автоматично на основі формату номера каталогу.',
    'de-ch': 'Automatische Erkennung anhand des Katalognummernformats.',
    'pt-br': 'Detecção automática com base no formato do número de catálogo.',
  },
  uniqueness: {
    'en-us': 'Uniqueness',
    'de-ch': 'Einzigartigkeit',
    'es-es': 'Unicidad',
    'fr-fr': 'Unicité',
    'pt-br': 'Singularidade',
    'ru-ru': 'Уникальность',
    'uk-ua': 'Унікальність',
  },
  use: {
    comment: 'Verb',
    'en-us': 'Use',
    'ru-ru': 'Использовать',
    'es-es': 'Usar',
    'fr-fr': 'Utiliser',
    'uk-ua': 'використання',
    'de-ch': 'Verwenden',
    'pt-br': 'Usar',
  },
  dontUse: {
    'en-us': 'Don’t use',
    'ru-ru': 'Не использовать',
    'es-es': 'No utilizar',
    'fr-fr': 'Zoom avec la molette de défilement',
    'uk-ua': 'Масштаб колеса прокрутки',
    'de-ch': 'Nicht verwenden',
    'pt-br': 'Não use',
  },
  position: {
    'en-us': 'Position',
    'es-es': 'Posición',
    'fr-fr': 'Position',
    'ru-ru': 'Позиция',
    'uk-ua': 'Позиція',
    'de-ch': 'Position',
    'pt-br': 'Posição',
  },
  top: {
    'en-us': 'Top',
    'es-es': 'Arriba',
    'fr-fr': 'Haut',
    'ru-ru': 'Вершина',
    'uk-ua': 'Топ',
    'de-ch': 'Spitze',
    'pt-br': 'Principal',
  },
  bottom: {
    'en-us': 'Bottom',
    'es-es': 'Abajo',
    'ru-ru': 'Нижний',
    'uk-ua': 'Дно',
    'de-ch': 'Unten',
    'fr-fr': 'Bas',
    'pt-br': 'Fundo',
  },
  left: {
    'en-us': 'Left',
    'es-es': 'Izquierda',
    'fr-fr': 'Gauche',
    'ru-ru': 'Левый',
    'uk-ua': 'Ліворуч',
    'de-ch': 'Links',
    'pt-br': 'Esquerda',
  },
  right: {
    'en-us': 'Right',
    'es-es': 'Bien',
    'fr-fr': 'Droite',
    'ru-ru': 'Верно',
    'uk-ua': 'правильно',
    'de-ch': 'Rechts',
    'pt-br': 'Certo',
  },
  showUnsavedIndicator: {
    'en-us': 'Show unsaved changes indicator',
    'ru-ru': 'Показать индикатор несохраненных изменений',
    'es-es': 'Mostrar indicador de cambios no guardados',
    'fr-fr': "Afficher l'indicateur de modifications non enregistrées",
    'uk-ua': 'Показати індикатор незбережених змін',
    'de-ch': 'Indikator für nicht gespeicherte Änderungen anzeigen',
    'pt-br': 'Mostrar indicador de alterações não salvas',
  },
  showUnsavedIndicatorDescription: {
    'en-us':
      'Show an "*" in the tab title when there are unsaved changes in the current tab.',
    'es-es':
      'Mostrar un "*" en el título de la pestaña cuando haya cambios sin guardar en la pestaña actual.',
    'fr-fr':
      "Afficher un \"*\" dans le titre de l'onglet lorsqu'il y a des modifications non enregistrées dans l'onglet actuel.",
    'ru-ru':
      'Отображать звездочку "*" в заголовке вкладки, если в текущей вкладке есть несохраненные изменения.',
    'uk-ua':
      'Показувати «*» у заголовку вкладки, якщо в поточній вкладці є незбережені зміни.',
    'de-ch':
      'Zeigen Sie ein "*" im Tab-Titel an, wenn im aktuellen Tab ungespeicherte Änderungen vorhanden sind.',
    'pt-br':
      'Exibir um "*" no título da aba quando houver alterações não salvas na aba atual.',
  },
  autoPopulateDescription: {
    'en-us':
      'Auto populate the merged record with values from duplicates when opening the merging dialog.',
    'ru-ru':
      'При открытии диалогового окна слияния объединенная запись автоматически заполняется значениями из дубликатов.',
    'de-ch':
      'Beim Öffnen des Zusammenführungsdialogs werden die Werte aus den Duplikaten automatisch in den zusammengeführten Datensatz übernommen.',
    'es-es':
      'Rellene automáticamente el registro fusionado con valores de duplicados al abrir el cuadro de diálogo de fusión.',
    'fr-fr':
      "Remplir automatiquement l'enregistrement fusionné avec les valeurs des doublons lors de l'ouverture de la boîte de dialogue de fusion.",
    'uk-ua':
      'Автоматичне заповнення об’єднаного запису значеннями з дублікатів під час відкриття діалогового вікна об’єднання.',
    'pt-br':
      'Preencha automaticamente o registro mesclado com os valores dos duplicados ao abrir a caixa de diálogo de mesclagem.',
  },
  autoCreateVariants: {
    'en-us': 'Automatically create {agentVariantTable:string} records',
    'ru-ru': 'Автоматически создавать записи {agentVariantTable:string}',
    'de-ch':
      'Automatische Erstellung von {agentVariantTable:string}-Datensätzen',
    'es-es': 'Crear automáticamente registros {agentVariantTable:string}',
    'fr-fr':
      'Créer automatiquement des enregistrements {agentVariantTable:string}',
    'uk-ua': 'Автоматично створювати записи {agentVariantTable:string}',
    'pt-br': 'Criar automaticamente registros {agentVariantTable:string}',
  },
  autoCreateVariantsDescription: {
    'en-us':
      'When merging agents, automatically create {agentVariantTable:string} records based on the variations of first name/last name.',
    'ru-ru':
      'При объединении агентов автоматически создавайте записи {agentVariantTable:string} на основе вариантов имени/фамилии.',
    'de-ch':
      'Beim Zusammenführen von Agenten werden automatisch {agentVariantTable:string}-Datensätze auf Basis der Varianten von Vor- und Nachnamen erstellt.',
    'es-es':
      'Al fusionar agentes, se crean automáticamente registros {agentVariantTable:string} basados en las variaciones de nombre/apellido.',
    'fr-fr':
      "Lors de la fusion d'agents, créez automatiquement des enregistrements {agentVariantTable:string} en fonction des variations du prénom/nom.",
    'uk-ua':
      'Під час об’єднання агентів автоматично створювати записи {agentVariantTable:string} на основі варіацій імені/прізвища.',
    'pt-br':
      'Ao mesclar agentes, crie automaticamente registros {agentVariantTable:string} com base nas variações de nome/sobrenome.',
  },
  collectionPreferences: {
    'en-us': 'Collection Preferences',
    'de-ch': 'Sammlungspräferenzen',
    'es-es': 'Preferencias de colección',
    'fr-fr': 'Personnalisation',
    'ru-ru': 'Настройки коллекции',
    'uk-ua': 'Налаштування',
    'pt-br': 'Preferências de Coleção',
  },
  rememberDialogSizes: {
    'en-us': 'Remember dialog window sizes',
    'ru-ru': 'Запомнить размеры диалоговых окон',
    'es-es': 'Recordar los tamaños de las ventanas de diálogo',
    'fr-fr': 'Mémoriser les tailles des fenêtres de dialogue',
    'uk-ua': "Запам'ятайте розміри діалогових вікон",
    'de-ch': 'Dialogfenstergrößen merken',
    'pt-br': 'Lembre-se dos tamanhos das janelas de diálogo.',
  },
  rememberDialogPositions: {
    'en-us': 'Remember dialog window positions',
    'ru-ru': 'Запомнить положение диалоговых окон',
    'es-es': 'Recordar las posiciones de las ventanas de diálogo',
    'fr-fr': 'Mémoriser les positions des fenêtres de dialogue',
    'uk-ua': "Запам'ятовуйте положення діалогового вікна",
    'de-ch': 'Dialogfensterpositionen merken',
    'pt-br': 'Lembre-se das posições das janelas de diálogo.',
  },
  autoPlayMedia: {
    'en-us': 'Automatically play media',
    'ru-ru': 'Автоматическое воспроизведение медиафайлов',
    'es-es': 'Reproducir automáticamente medios',
    'fr-fr': 'Lire automatiquement les médias',
    'uk-ua': 'Автоматичне відтворення медіа',
    'de-ch': 'Medien automatisch abspielen',
    'pt-br': 'Reproduzir mídia automaticamente',
  },
  useCustomTooltips: {
    'en-us': 'Use modern tooltips',
    'ru-ru': 'Используйте современные всплывающие подсказки.',
    'es-es': 'Utilice información sobre herramientas moderna',
    'fr-fr': 'Utiliser des info-bulles modernes',
    'uk-ua': 'Використовуйте сучасні підказки',
    'de-ch': 'Moderne Tooltips verwenden',
    'pt-br': 'Use dicas de ferramentas modernas',
  },
  alwaysUseQueryBuilder: {
    'en-us': 'Always use query builder search inside of search form',
    'de-ch':
      'Verwenden Sie immer die Abfragegenerator-Suche innerhalb des Suchformulars.',
    'es-es':
      'Utilice siempre la búsqueda del generador de consultas dentro del formulario de búsqueda',
    'fr-fr':
      'Utilisez toujours la recherche du générateur de requêtes dans le formulaire de recherche',
    'ru-ru':
      'Всегда используйте поиск с помощью построителя запросов внутри формы поиска.',
    'uk-ua': 'Завжди використовуйте пошук конструктора запитів у формі пошуку',
    'pt-br':
      'Sempre utilize o construtor de consultas dentro do formulário de pesquisa.',
  },
  localizeResourceNames: {
    'en-us': 'Localize the names of recognized app resources',
    'de-ch': 'Lokalisieren Sie die Namen der erkannten App-Ressourcen',
    'es-es':
      'Localizar los nombres de los recursos de aplicaciones reconocidos',
    'fr-fr': "Localiser les noms des ressources d'application reconnues",
    'ru-ru': 'Локализуйте названия распознанных ресурсов приложения.',
    'uk-ua': 'Локалізувати назви розпізнаних ресурсів програми',
    'pt-br': 'Localize os nomes dos recursos de aplicativos reconhecidos.',
  },
  splitLongXml: {
    'en-us': 'Split long lines of XML into multiple lines',
    'de-ch': 'Lange XML-Zeilen in mehrere Zeilen aufteilen',
    'es-es': 'Dividir líneas largas de XML en varias líneas',
    'fr-fr': 'Diviser les longues lignes de XML en plusieurs lignes',
    'ru-ru': 'Разделите длинные строки XML на несколько строк.',
    'uk-ua': 'Розділіть довгі рядки XML на кілька рядків',
    'pt-br': 'Dividir linhas longas de XML em várias linhas.',
  },
  url: {
    'en-us': 'URL',
    'de-ch': 'URL',
    'es-es': 'URL',
    'fr-fr': 'URL',
    'uk-ua': 'URL',
    'ru-ru': 'URL',
    'pt-br': 'URL',
  },
  pickAttachment: {
    'en-us': 'Pick an attachment',
    'es-es': 'Elige un archivo adjunto',
    'fr-fr': 'Choisissez une pièce jointe',
    'ru-ru': 'Выберите вложение',
    'uk-ua': 'Виберіть вкладення',
    'de-ch': 'Wählen Sie einen Anhang aus',
    'pt-br': 'Selecione um anexo',
  },
  attachmentFailed: {
    'en-us': 'The attachment failed to load.',
    'de-ch': 'Der Anhang konnte nicht geladen werden.',
    'es-es': 'No se pudo cargar el archivo adjunto.',
    'fr-fr': "La pièce jointe n'a pas pu être chargée.",
    'ru-ru': 'Вложение не загрузилось.',
    'uk-ua': 'Не вдалося завантажити вкладений файл.',
    'pt-br': 'O anexo não carregou.',
  },
  pickImage: {
    'en-us': 'Pick an image',
    'de-ch': 'Wähle ein Bild aus',
    'es-es': 'Elige una imagen',
    'fr-fr': 'Choisissez une image',
    'ru-ru': 'Выберите изображение',
    'uk-ua': 'Виберіть зображення',
    'pt-br': 'Escolha uma imagem',
  },
  customLogo: {
    'en-us': 'Expanded Image URL',
    'de-ch': 'Erweiterte Bild-URL',
    'es-es': 'URL de imagen expandida',
    'fr-fr': "URL de l'image étendue",
    'ru-ru': 'Расширенная ссылка на изображение',
    'uk-ua': 'Розширена URL-адреса зображення',
    'pt-br': 'URL da imagem expandida',
  },
  customLogoCollapsed: {
    'en-us': 'Collapsed Image URL',
    'de-ch': 'URL des zusammengefassten Bildes',
    'es-es': 'URL de imagen contraída',
    'fr-fr': "URL de l'image réduite",
    'ru-ru': 'Свернутая ссылка на изображение',
    'uk-ua': 'URL-адреса згорнутого зображення',
    'pt-br': 'URL da imagem recolhida',
  },
  customLogoDescription: {
    'en-us':
      'A URL to an image that would be displayed next to the Specify logo in the navigation menu.',
    'de-ch':
      'Eine URL zu einem Bild, das neben dem Specify-Logo im Navigationsmenü angezeigt werden soll.',
    'es-es':
      'Una URL a una imagen que se mostrará junto al logotipo Especificar en el menú de navegación.',
    'fr-fr':
      'Une URL vers une image qui serait affichée à côté du logo Specify dans le menu de navigation.',
    'ru-ru':
      'URL-адрес изображения, которое будет отображаться рядом с логотипом «Указать» в навигационном меню.',
    'uk-ua':
      'URL-адреса зображення, яке відображатиметься поруч із «Вказати логотип» у меню навігації.',
    'pt-br':
      'Um URL para uma imagem que será exibida ao lado do logotipo "Especificar" no menu de navegação.',
  },
  showLineNumber: {
    'en-us': 'Show query result line number',
    'de-ch': 'Zeilennummer des Abfrageergebnisses anzeigen',
    'es-es': 'Mostrar el número de línea del resultado de la consulta',
    'fr-fr': 'Afficher le numéro de ligne du résultat de la requête',
    'ru-ru': 'Показать номер строки результата запроса',
    'uk-ua': 'Показати номер рядка результату запиту',
    'pt-br': 'Exibir número da linha do resultado da consulta',
  },
  saveButtonColor: {
    'en-us': 'Save button color',
    'de-ch': 'Farbe der Schaltfläche „Speichern“',
    'es-es': 'Guardar color del botón',
    'fr-fr': 'Couleur du bouton Enregistrer',
    'ru-ru': 'Сохранить цвет кнопки',
    'uk-ua': 'Зберегти колір кнопки',
    'pt-br': 'Cor do botão Salvar',
  },
  secondaryButtonColor: {
    'en-us': 'Secondary button color',
    'es-es': 'Color del botón secundario',
    'fr-fr': 'Couleur du bouton secondaire',
    'ru-ru': 'Вторичный цвет кнопки',
    'uk-ua': 'Колір вторинної кнопки',
    'de-ch': 'Sekundäre Tastenfarbe',
    'pt-br': 'Cor do botão secundário',
  },
  secondaryLightButtonColor: {
    'en-us': 'Secondary light button color',
    'de-ch': 'Farbe des sekundären Lichtknopfes',
    'es-es': 'Color del botón de luz secundaria',
    'fr-fr': "Couleur du bouton d'éclairage secondaire",
    'ru-ru': 'Цвет кнопки вторичного освещения',
    'uk-ua': 'Колір вторинної світлової кнопки',
    'pt-br': 'Cor do botão de luz secundário',
  },
  dangerButtonColor: {
    'en-us': 'Danger button color',
    'de-ch': 'Farbe des Gefahrenknopfes',
    'es-es': 'Color del botón de peligro',
    'fr-fr': 'Couleur du bouton de danger',
    'ru-ru': 'Цвет кнопки «Опасность»',
    'uk-ua': 'Колір кнопки небезпеки',
    'pt-br': 'Cor do botão de perigo',
  },
  infoButtonColor: {
    'en-us': 'Info button color',
    'de-ch': 'Farbe der Info-Schaltfläche',
    'es-es': 'Color del botón de información',
    'fr-fr': "Couleur du bouton d'information",
    'ru-ru': 'Цвет кнопки информации',
    'uk-ua': 'Колір інформаційної кнопки',
    'pt-br': 'Cor do botão de informações',
  },
  warningButtonColor: {
    'en-us': 'Warning button color',
    'de-ch': 'Farbe der Warnschaltfläche',
    'es-es': 'Color del botón de advertencia',
    'fr-fr': "Couleur du bouton d'avertissement",
    'ru-ru': 'Цвет кнопки предупреждения',
    'uk-ua': 'Колір кнопки попередження',
    'pt-br': 'Cor do botão de aviso',
  },
  successButtonColor: {
    'en-us': 'Success button color',
    'de-ch': 'Farbe der Erfolgsschaltfläche',
    'es-es': 'Color del botón de éxito',
    'fr-fr': 'Couleur du bouton de réussite',
    'ru-ru': 'Цвет кнопки «Успех»',
    'uk-ua': 'Колір кнопки успіху',
    'pt-br': 'Cor do botão de sucesso',
  },
  openAsReadOnly: {
    'en-us': 'Open all records in read-only mode',
    'de-ch': 'Alle Datensätze im Nur-Lese-Modus öffnen',
    'es-es': 'Abrir todos los registros en modo de solo lectura',
    'fr-fr': 'Ouvrir tous les enregistrements en mode lecture seule',
    'ru-ru': 'Открыть все записи в режиме только для чтения',
    'uk-ua': 'Відкрити всі записи в режимі лише для читання',
    'pt-br': 'Abrir todos os registros em modo somente leitura',
  },
  displayBasicView: {
    'en-us': 'Display basic view',
    'de-ch': 'Grundlegende Ansicht anzeigen',
    'es-es': 'Mostrar vista básica',
    'fr-fr': 'Afficher la vue de base',
    'ru-ru': 'Отображение базового вида',
    'uk-ua': 'Відобразити базовий вигляд',
    'pt-br': 'Exibir visualização básica',
  },
  showComparisonOperatorsForString: {
    'en-us': 'Show comparison operators for text-based fields',
    'de-ch': 'Vergleichsoperatoren für textbasierte Felder anzeigen',
    'es-es': 'Mostrar operadores de comparación para campos basados en texto',
    'fr-fr': 'Afficher les opérateurs de comparaison pour les champs textuels',
    'pt-br': 'Exibir operadores de comparação para campos baseados em texto.',
    'ru-ru': 'Показать операторы сравнения для текстовых полей',
    'uk-ua': 'Показати оператори порівняння для текстових полів',
  },
  showComparisonOperatorsDescription: {
    'en-us':
      'Allows the following filters to apply to text fields: Greater Than, Less Than, Greater Than or Equal to, and Less Than or Equal to',
    'de-ch':
      'Ermöglicht die Anwendung der folgenden Filter auf Textfelder: Größer als, Kleiner als, Größer oder gleich und Kleiner oder gleich',
    'es-es':
      'Permite aplicar los siguientes filtros a los campos de texto: Mayor que, Menor que, Mayor o igual que y Menor o igual que',
    'fr-fr':
      "Permet d'appliquer les filtres suivants aux champs de texte : supérieur à, inférieur à, supérieur ou égal à et inférieur ou égal à",
    'pt-br':
      'Permite que os seguintes filtros sejam aplicados aos campos de texto: Maior que, Menor que, Maior ou igual a e Menor ou igual a.',
    'ru-ru':
      'Позволяет применять к текстовым полям следующие фильтры: Больше, Меньше, Больше или равно и Меньше или равно',
    'uk-ua':
      'Дозволяє застосовувати до текстових полів такі фільтри: «Більше ніж», «Менше ніж», «Більше або дорівнює» та «Менше або дорівнює»',
  },
  basicView: {
    'en-us': 'Basic view',
    'de-ch': 'Grundansicht',
    'es-es': 'Vista básica',
    'fr-fr': 'Vue de base',
    'ru-ru': 'Базовый вид',
    'uk-ua': 'Основний вигляд',
    'pt-br': 'Visão básica',
  },
  detailedView: {
    'en-us': 'Detailed view',
    'de-ch': 'Detailansicht',
    'es-es': 'Vista detallada',
    'fr-fr': 'Vue détaillée',
    'ru-ru': 'Детальный вид',
    'uk-ua': 'Детальний вигляд',
    'pt-br': 'Visão detalhada',
  },
  attachmentPreviewMode: {
    'en-us': 'Attachment preview mode',
    'de-ch': 'Anhangsvorschaumodus',
    'es-es': 'Modo de vista previa de archivos adjuntos',
    'fr-fr': 'Mode aperçu des pièces jointes',
    'ru-ru': 'режим предварительного просмотра вложений',
    'uk-ua': 'Режим попереднього перегляду вкладених файлів',
    'pt-br': 'Modo de pré-visualização de anexos',
  },
  fullResolution: {
    'en-us': 'Full Resolution',
    'de-ch': 'Volle Auflösung',
    'es-es': 'Resolución completa',
    'fr-fr': 'Résolution maximale',
    'ru-ru': 'Полное разрешение',
    'uk-ua': 'Повна роздільна здатність',
    'pt-br': 'Resolução completa',
  },
  thumbnail: {
    'en-us': 'Thumbnail',
    'de-ch': 'Miniaturansicht',
    'es-es': 'Uña del pulgar',
    'fr-fr': 'Vignette',
    'ru-ru': 'Миниатюра',
    'uk-ua': 'Мініатюра',
    'pt-br': 'Miniatura',
  },
  addSearchBarHomePage: {
    'en-us': 'Add Search Bar on home page',
    'de-ch': 'Suchleiste auf der Startseite hinzufügen',
    'es-es': 'Agregar barra de búsqueda en la página de inicio',
    'fr-fr': "Ajouter une barre de recherche sur la page d'accueil",
    'ru-ru': 'Добавить строку поиска на главную страницу',
    'uk-ua': 'Додайте рядок пошуку на головну сторінку',
    'pt-br': 'Adicionar barra de pesquisa na página inicial',
  },
  inheritanceCatNumberPrefDescription: {
    'en-us':
      'Only {collectionObjectTableName: string} records without a {catalogNumberFieldName: string} will inherit one from the primary {collectionObjectTableName: string} in a {collectionObjectGroupTableName: string}.',
    'de-ch':
      'Nur {collectionObjectTableName: string}-Datensätze ohne {catalogNumberFieldName: string} erben einen solchen vom primären {collectionObjectTableName: string} in einer {collectionObjectGroupTableName: string}.',
    'es-es':
      'Solo los registros {collectionObjectTableName: string} sin un {catalogNumberFieldName: string} heredarán uno del {collectionObjectTableName: string} principal en un {collectionObjectGroupTableName: string}.',
    'fr-fr':
      "Seuls les enregistrements {collectionObjectTableName: string} sans {catalogNumberFieldName: string} hériteront d'un {collectionObjectTableName: string} principal dans un {collectionObjectGroupTableName: string}.",
    'pt-br':
      'Somente os registros {collectionObjectTableName: string} sem um {catalogNumberFieldName: string} herdarão um do {collectionObjectTableName: string} primário em um {collectionObjectGroupTableName: string}.',
    'ru-ru':
      'Только записи {collectionObjectTableName: string}, не имеющие поля {catalogNumberFieldName: string}, унаследуют его от основного поля {collectionObjectTableName: string} в таблице {collectionObjectGroupTableName: string}.',
    'uk-ua':
      'Тільки записи {collectionObjectTableName: string} без {catalogNumberFieldName: string} успадкують його від основного {collectionObjectTableName: string} в {collectionObjectGroupTableName: string}.',
  },
  inheritanceCatNumberPref: {
    'en-us':
      'Enable {catalogNumberFieldName: string} inheritance for {collectionObjectGroupTableName: string} siblings',
    'de-ch':
      'Aktivieren Sie die Vererbung von {catalogNumberFieldName: string} für die Geschwistertabellen {collectionObjectGroupTableName: string}.',
    'es-es':
      'Habilitar la herencia de {catalogNumberFieldName: string} para los hermanos {collectionObjectGroupTableName: string}',
    'fr-fr':
      "Activer l'héritage de {catalogNumberFieldName: string} pour les éléments frères de {collectionObjectGroupTableName: string}",
    'pt-br':
      'Habilitar herança de {catalogNumberFieldName: string} para itens irmãos de {collectionObjectGroupTableName: string}',
    'ru-ru':
      'Включить наследование {catalogNumberFieldName: string} для соседних элементов {collectionObjectGroupTableName: string}',
    'uk-ua':
      'Увімкнути успадкування основного {catalogNumberFieldName: string} його порожнім братам і сестрам',
  },
  inheritanceCatNumberParentCOPref: {
    'en-us':
      'Enable {catalogNumberFieldName: string} inheritance for {componentTableName: string} records',
    'de-ch':
      'Aktivieren Sie die Vererbung von {catalogNumberFieldName: string} für {componentTableName: string}-Datensätze.',
    'es-es':
      'Habilitar la herencia de {catalogNumberFieldName: string} para los registros de {componentTableName: string}',
    'fr-fr':
      "Activer l'héritage {catalogNumberFieldName: string} pour les enregistrements {componentTableName: string}",
    'pt-br':
      'Habilitar herança de {catalogNumberFieldName: string} para registros de {componentTableName: string}',
    'ru-ru':
      'Включить наследование {catalogNumberFieldName: string} для записей {componentTableName: string}',
    'uk-ua':
      'Увімкніть успадкування {catalogNumberFieldName: string} для записів {componentTableName: string}',
  },
  uniqueCatNumberAcrossCompAndCo: {
    'en-us':
      'Enforce uniqueness for the {catalogNumberFieldName: string} field across {componentTableName: string} and {collectionObjectTableName: string}.',
    'de-ch':
      'Erzwinge die Eindeutigkeit des Feldes {catalogNumberFieldName: string} in den Tabellen {componentTableName: string} und {collectionObjectTableName: string}.',
    'es-es':
      'Aplicar unicidad para el campo {catalogNumberFieldName: string} en {componentTableName: string} y {collectionObjectTableName: string}.',
    'fr-fr':
      "Imposer l'unicité du champ {catalogNumberFieldName: string} entre {componentTableName: string} et {collectionObjectTableName: string}.",
    'pt-br':
      'Garanta a unicidade do campo {catalogNumberFieldName: string} em {componentTableName: string} e {collectionObjectTableName: string}.',
    'ru-ru':
      'Обеспечьте уникальность поля {catalogNumberFieldName: string} для полей {componentTableName: string} и {collectionObjectTableName: string}.',
    'uk-ua':
      'Забезпечити унікальність для поля {catalogNumberFieldName: string} у {componentTableName: string} та {collectionObjectTableName: string}.',
  },
  scopeEntireTablePicklists: {
    'en-us': 'Scope "Entire Table" Picklists',
    'de-ch': 'Bereich „Gesamte Tabelle“ Auswahllisten',
    'es-es': 'Listas de selección de "Tabla completa" de Scope',
    'fr-fr': 'Listes de sélection « Tableau entier »',
    'pt-br': 'Escopo "Tabela Inteira" Listas de seleção',
    'ru-ru': 'Область действия: Списки выбора "Вся таблица"',
    'uk-ua': 'Списки вибору «Уся таблиця»',
  },
  scopeEntireTablePicklistsDescription: {
    'en-us':
      'If enabled, picklists of type "Entire Table" will only show items that are in use within the current collection.',
    'de-ch':
      'Wenn diese Option aktiviert ist, werden in Auswahllisten vom Typ „Gesamte Tabelle“ nur Elemente angezeigt, die in der aktuellen Sammlung verwendet werden.',
    'es-es':
      'Si esta opción está habilitada, las listas de selección de tipo "Tabla completa" solo mostrarán los elementos que estén en uso dentro de la colección actual.',
    'fr-fr':
      "Si cette option est activée, les listes de sélection de type « Tableau entier » n'afficheront que les éléments utilisés dans la collection actuelle.",
    'pt-br':
      'Se ativada, a opção "Lista de opções" do tipo "Tabela inteira" exibirá apenas os itens que estão em uso na coleção atual.',
    'ru-ru':
      'Если эта функция включена, в выпадающих списках типа «Вся таблица» будут отображаться только элементы, используемые в текущей коллекции.',
    'uk-ua':
      'Якщо ввімкнено, у списках вибору типу «Уся таблиця» відображатимуться лише ті елементи, які використовуються в поточній колекції.',
  },
  inheritanceCatNumberParentCOPrefDescription: {
    'en-us':
      'Only {componentTableName: string} records without a {catalogNumberFieldName: string} will inherit one from its parent {collectionObjectTableName: string}.',
    'de-ch':
      'Nur Datensätze mit dem Namen {componentTableName: string}, die kein {catalogNumberFieldName: string} aufweisen, erben ein solches von ihrem übergeordneten Datensatz {collectionObjectTableName: string}.',
    'es-es':
      'Solo los registros {componentTableName: string} sin un {catalogNumberFieldName: string} heredarán uno de su padre {collectionObjectTableName: string}.',
    'fr-fr':
      'Seuls les enregistrements {componentTableName: string} sans {catalogNumberFieldName: string} hériteront de celui de leur {collectionObjectTableName: string} parent.',
    'pt-br':
      'Somente os registros {componentTableName: string} sem um {catalogNumberFieldName: string} herdarão um de seu pai {collectionObjectTableName: string}.',
    'ru-ru':
      'Только записи {componentTableName: string}, не имеющие поля {catalogNumberFieldName: string}, унаследуют его от родительской таблицы {collectionObjectTableName: string}.',
    'uk-ua':
      'Тільки записи {componentTableName: string} без {catalogNumberFieldName: string} успадкують його від батьківського {collectionObjectTableName: string}.',
  },
} as const);
